import IconContainer from '@/components/common/tabs/IconContainer';
import GiftIcon from '@/components/icons/GiftIcon';
import ListIcon from '@/components/icons/ListIcon';
import QuestionIcon from '@/components/icons/QuestionIcon';
import TutorialIcon from '@/components/icons/TutorialIcon';
import UsersIcon from '@/components/icons/UsersIcon';
import VideoIcon from '@/components/icons/VideoIcon';
import SlidesList from '@/components/pages/live/admin/SlidesList';
import CurvedCorner from '@/components/ui/CurvedCorner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useKeepMountedOnce } from '@/hooks/useKeepMountedOnce';
import { Hourglass, Info, Paperclip, ScreenShareIcon, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { useAdminChatRole, useChannelName } from '@/routes/AblyAuth';
import { useAbly, useChannel } from 'ably/react';
import {
  fetchWebinarById,
  getWebinarSession,
  listOfferTemplates,
  listPresenters,
  listQuizTemplates,
  listSpeakers,
  listSurveyTemplates,
  listWebinarResources,
  listWebinarSlides,
  updateWebinar,
  adminHideSharedResource,
} from '@/features/webinar/webinarThunks';
import type { OfferTemplate, SurveyTemplate, WebinarResource } from '@/features/webinar/types';
import VideosList from '@/components/pages/live/admin/VideosList';
import FooterBar from '@/components/pages/live/FootBar';
import LiveResourceBadge from '@/components/pages/live/LiveResourceBadge';
import SurveysList from '@/components/pages/live/admin/SurveysList';
import QuizsList from '@/components/pages/live/admin/QuizsList';
import OfferList from '@/components/pages/live/admin/OfferList';
import SharedResourceLiveList from '@/components/pages/live/admin/SharedResourceLiveList';
import { applySharedResourceLiveEvent } from '@/features/webinar/webinarSlice';
// import SpeakersList from '@/components/pages/live/admin/SpeakersList';
import ChatView from '@/components/pages/live/ChatView';
import { Button, buttonVariants } from '@/components/ui/button';
import { fetchUsage } from '@/features/billing/billingThunks';
import { selectUsage } from '@/features/billing/billingSlice';
import ChangePlanDialog from '@/components/billing/ChangePlanDialog';
import SubscriptionRequiredModal from '@/components/billing/SubscriptionRequiredModal';
import UsageExpiredAlert from '@/components/billing/UsageExpiredAlert';
import { isLiveRoomFull } from '@/components/billing/usageExpiredUtils';
import { isWorkspaceFreeWithoutLiveAccess } from '@/features/workspace/workspaceSlice';
import {
  closeSession,
  fetchAdminResourceState,
  fetchModeratorToken,
  fetchPlayback,
  fetchPresenterToken,
  fetchQuizResults,
  fetchOfferResults,
  fetchSurveyResults,
  refreshEvergreenChatMode,
  startCustomRecording,
  // startSession,
  triggerAdminEvent,
  updateSessionLayout,
} from '@/features/live/liveThunks';
import { applyEvergreenChatModeChange, setStreamStartStatus } from '@/features/live/liveSlice';
import {
  evergreenChatModeCommand,
  readChatModeChange,
  readChatModeVersion,
  regieDisplayedSettings,
  regieSettingsToSave,
  replayedChatModeFor,
} from '@/lib/evergreenChatMode';
import type { LiveChatMode } from '@/features/live/ablySessionChannels';
import LiveLaunchOverlay from '@/components/pages/live/LiveLaunchOverlay';
import LiveConfirmedToast from '@/components/pages/live/LiveConfirmedToast';
import LiveDurationToast from '@/components/pages/live/LiveDurationToast';
import { isSessionDurationExceeded } from '@/lib/liveDurationNotice';
import { isLiveConfirmed, shouldRestoreLiveOnReload } from '@/lib/liveReadiness';
import {
  shouldRecordStaffPresence,
  shouldRequestModeratorLiveKitToken,
  shouldRetryStaffPresence,
  STAFF_PRESENCE_MAX_RETRIES,
  STAFF_PRESENCE_RETRY_DELAY_MS,
} from '@/lib/staffSessionPresence';
import { liveService } from '@/services/live.service';
import { resourcesToHideOnShow, INTERACTION_RESOURCE_TYPES } from '@/lib/liveResourceExclusivity';
import type {
  LiveKitLayoutInput,
  LiveSessionResourceStateDto,
  ModeratorToken,
  TriggerEventInput,
} from '@/features/live/types';
import type {
  VideoLayoutState,
  VideoPlayerStateControl,
} from '@/components/pages/live/admin/VideoLiveView';
import { buildVideoActiveAsset } from '@/lib/liveVideoLayout';
import {
  clearScreenShareIntent,
  getScreenShareIntentAt,
  markScreenShareIntent,
  reconcileScreenShareIntent,
  shouldOfferScreenShareResume,
} from '@/lib/liveScreenShareMemory';
import { LiveKitRoom, type LiveKitScreenShareControl } from '@/components/pages/live/LiveKitRoom';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import SplashScreen from '@/components/common/SplashScreen';
import AttendeesListView from '@/components/pages/live/admin/AttendeesListView';
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import Title from '@/components/ui/Title';
import {
  clearStoredAdminPdfPagesForSession,
  getStoredAdminPdfPage,
  setStoredAdminPdfPage,
} from '@/lib/liveAdminPdfPageMemory';
import { cn } from '@/lib/utils';
import { parsePresenceUpdateLiveCount } from '@/lib/presenceUpdate';
import {
  MediaDeviceTester,
  type MediaDeviceSelection,
} from '@/components/pages/live/admin/MediaDeviceTester';
import { PretestDevicesProvider } from '@/contexts/PretestDevicesContext';
import YouTubeHostStage from '@/components/pages/live/admin/YouTubeHostStage';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import PresentersList from '@/components/pages/live/admin/PresentersList';
import { useStageInviteEvents } from '@/components/pages/live/admin/useStageInviteEvents';
import {
  AlertDialog,
  AlertDialogTitle,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogFooter,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import ModeratorLivePage from './ModeratorLivePage';
import CamLayoutProvider from '@/features/live/CamLayoutProvider';
import { DateTime } from 'luxon';
import { formatPlannedLiveDate } from '@/lib/formatPlannedDate';
import { createLiveEventDeduplicator, normalizeLiveBatchMessage } from '@/lib/liveEventBatch';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { RoomEvent, Track, type LocalTrackPublication, type Room } from 'livekit-client';

export function getConditionalBackgroundStyle({
  theme,
  defaultIdentity,
  defaultIdentityColor,
  isActive,
  isModeratorLive,
}: {
  theme: 'LIGHT' | 'DARK';
  defaultIdentity?: boolean;
  defaultIdentityColor?: string;
  isActive?: boolean;
  isModeratorLive?: boolean;
}) {
  if (defaultIdentity === true && isActive === true) {
    // Surface de l'onglet/panneau actif : on réutilise la surface secondaire du thème,
    // plus claire que le fond, pour une élévation lisible en clair comme en sombre.
    // Auparavant '#000' en dur : invisible en clair, et trop proche du fond navy en sombre.
    return {
      backgroundColor: defaultIdentityColor || (theme === 'LIGHT' ? '#F3F3F4' : '#1B1E3B'),
    };
  }
  if (isModeratorLive === true && isActive === true) {
    return {
      backgroundColor: defaultIdentityColor || '#C0D3FF',
    };
  }

  return {
    backgroundColor: defaultIdentityColor + '20',
  };
}

export function getConditionalIconColor({
  defaultIdentity,
  secondaryBackgroundColor,
}: {
  defaultIdentity?: boolean;
  secondaryBackgroundColor?: string | null;
}) {
  if (defaultIdentity === true) {
    return '#2C6CFF';
  }
  return secondaryBackgroundColor || '#2C6CFF';
}

/** Après refresh / start enregistrement, l’API peut renvoyer la slide avec `live: false` mais `lastStatePayload` encore renseigné. */
function pickSlideStateForReapply(
  resourceStates: LiveSessionResourceStateDto[],
  slideHint: LiveSessionResourceStateDto | null,
): LiveSessionResourceStateDto | null {
  const slides = resourceStates.filter((s) => s.resourceType === 'WEBINAR_SLIDE');
  const liveSlide = slides.find((s) => s.live);
  if (liveSlide) return liveSlide;
  if (slideHint?.resourceType === 'WEBINAR_SLIDE') return slideHint;
  const withPage = slides.find(
    (s) =>
      s.lastStatePayload != null &&
      typeof (s.lastStatePayload as { pageNumber?: unknown }).pageNumber === 'number',
  );
  if (withPage) return withPage;
  return null;
}

function captureSlideHintFromStore(
  states: LiveSessionResourceStateDto[],
): LiveSessionResourceStateDto | null {
  return (
    states.find((s) => s.resourceType === 'WEBINAR_SLIDE' && s.live) ??
    states.find(
      (s) =>
        s.resourceType === 'WEBINAR_SLIDE' &&
        s.lastStatePayload != null &&
        typeof (s.lastStatePayload as { pageNumber?: unknown }).pageNumber === 'number',
    ) ??
    null
  );
}

/** True uniquement si ce document a été chargé par rechargement intégral (F5), pas en navigation client-side. */
function isDocumentReloadNavigation(): boolean {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) {
    return false;
  }
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  return entry?.type === 'reload';
}

/** Erreurs `getDisplayMedia` / LiveKit (`cause`) → toast ; NotAllowedError « denied by user » souvent = fermeture du sélecteur (Chrome). */
function classifyScreenShareStartFailure(err: unknown): 'cancelled' | 'permission' | 'generic' {
  const looksLikeUserDismissed = /denied by user|permission denied by user|user cancel(?:led|ed)/i;
  for (let e: unknown = err, depth = 0; e instanceof Error && depth < 12; e = e.cause, depth++) {
    const { name, message } = e;
    if (name === 'AbortError') return 'cancelled';
    if (name === 'NotAllowedError')
      return looksLikeUserDismissed.test(message ?? '') ? 'cancelled' : 'permission';
    if (name === 'SecurityError' || name === 'NotReadableError' || name === 'NotFoundError')
      return 'permission';
  }
  return 'generic';
}

// Aligné sur l'avertissement du back, qui se déclenche 15 minutes avant la coupure automatique.
const SESSION_ENDING_SOON_SECONDS = 15 * 60;

export default function AdminLivePage() {
  const { t, i18n } = useTranslation('webinars');
  const { t: tCommon } = useTranslation('common');
  const { webinarId: webinarIdParam, sessionId: sessionIdParam } = useParams<{
    webinarId: string;
    sessionId: string;
  }>();
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);

  const sessionId = sessionIdParam ? parseInt(sessionIdParam, 10) : null;
  const webinarId = webinarIdParam ? parseInt(webinarIdParam, 10) : null;
  const workspaceId = currentWorkspace?.id;
  // Compte gratuit sans accès live : on bloque le démarrage et on propose de s'abonner.
  const isFreeWithoutLiveAccess = isWorkspaceFreeWithoutLiveAccess(currentWorkspace);

  const channelName = useChannelName();
  useStageInviteEvents(channelName);
  const adminChatRole = useAdminChatRole();
  const ably = useAbly();

  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const {
    webinarSlides,
    webinarSlidesLoading,
    webinarResources,
    webinarResourcesLoading,
    surveyTemplates,
    surveyTemplatesLoading,
    quizTemplates,
    offerTemplates,
    presenters,
    speakers,
    presentersLoading,
    currentWebinarSession,
    currentWebinar,
  } = useAppSelector((state: RootState) => state.webinar);

  const {
    presenterToken,
    moderatorToken,
    startSessionLoading,
    startCustomRecordingLoading,
    streamStartStatus,
    playbackInfo,
    adminResourceStates,
    evergreenChatModeSync,
  } = useAppSelector((state: RootState) => state.live);

  const usage = useAppSelector(selectUsage);

  const isYouTube = currentWebinar?.transmissionMode === 'YOUTUBE';

  const [liveParticipantCount, setLiveParticipantCount] = useState(0);

  const [activeQuestion, setActiveQuestion] = useState<boolean>(false);
  const [activeFolder, setActiveFolder] = useState<boolean>(false);
  const [activeGift, setActiveGift] = useState<boolean>(false);
  const [activePresenters, setActivePresenters] = useState<boolean>(false);
  const [activeVideos, setActiveVideos] = useState<boolean>(false);
  const [activeSlides, setActiveSlides] = useState<boolean>(false);
  /** Les miniatures rechargent tous les PDF à chaque montage : on ne démonte plus après la 1re ouverture. */
  const slidesMounted = useKeepMountedOnce(activeSlides);
  const [activeShareScreen, setActiveShareScreen] = useState<boolean>(false);
  /** Piste de partage réellement active (false aussi si arrêt depuis le bandeau OS / navigateur). */
  const [isScreenShareLive, setIsScreenShareLive] = useState(false);
  const screenShareControlRef = useRef<LiveKitScreenShareControl | null>(null);
  const videoPlayerStateRef = useRef<VideoPlayerStateControl | null>(null);
  /** Une seule proposition de reprise par interruption, sinon chaque reconnexion empile un toast. */
  const screenShareResumeOfferedRef = useRef(false);
  /** Distingue une dépublication subie (page en cours de fermeture) d'un arrêt voulu du partage. */
  const isUnloadingRef = useRef(false);

  useEffect(() => {
    const markUnloading = () => {
      isUnloadingRef.current = true;
    };
    window.addEventListener('pagehide', markUnloading);
    window.addEventListener('beforeunload', markUnloading);
    return () => {
      window.removeEventListener('pagehide', markUnloading);
      window.removeEventListener('beforeunload', markUnloading);
    };
  }, []);
  const [liveKitRoomInstance, setLiveKitRoomInstance] = useState<Room | null>(null);

  const [activeLike, setActiveLike] = useState<boolean>(false);
  const [activeList, setActiveList] = useState<boolean>(false);
  const [currentReactions, setCurrentReactions] = useState<
    Array<{ id: string; text: string; left: number }>
  >([]);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<string>('chat');
  const theme = currentWorkspace?.visualIdentity?.theme || 'LIGHT';
  const secondaryBackgroundColor =
    currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#6DFFCD';
  const contrastColor = currentWorkspace?.visualIdentity?.contrastColor || '#F3F3F4';
  const [preliveLive, setPreliveLive] = useState<boolean>(false);
  const [checkedSetup, setCheckedSetup] = useState<boolean>(false);
  const liveConfirmed = isLiveConfirmed(streamStartStatus);
  const sawStreamPendingRef = useRef(false);
  const prevLiveConfirmedRef = useRef(liveConfirmed);
  const liveEventDeduplicatorRef = useRef(createLiveEventDeduplicator());
  // L'overlay d'échec peut être fermé pour reprendre la main sur « Démarrer la session » ;
  // on le réarme à chaque nouvelle tentative (retour en PENDING).
  const [launchFailedDismissed, setLaunchFailedDismissed] = useState(false);
  useEffect(() => {
    if (streamStartStatus === 'PENDING') {
      sawStreamPendingRef.current = true;
      setLaunchFailedDismissed(false);
    }
    if (liveConfirmed && !prevLiveConfirmedRef.current && sawStreamPendingRef.current) {
      toast.success(<LiveConfirmedToast />);
    }
    prevLiveConfirmedRef.current = liveConfirmed;
  }, [liveConfirmed, streamStartStatus]);

  // `batch-events` peut rejouer un même événement : on n'affiche le toast qu'une fois.
  const durationExceededShownRef = useRef(false);

  // Rechargement en cours de live : le back reste IN_PROGRESS mais le streamStartStatus (client)
  // repart de IDLE → on ré-hydrate depuis la vérité back pour restaurer le mode live (timer +
  // Clôturer). Ne se déclenche jamais pendant un lancement : à ce moment streamStartStatus vaut
  // déjà PENDING, pas IDLE.
  useEffect(() => {
    if (shouldRestoreLiveOnReload(currentWebinarSession?.status, streamStartStatus)) {
      dispatch(setStreamStartStatus('READY'));
    }
  }, [currentWebinarSession?.status, streamStartStatus, dispatch]);
  const [showJoinRequestModal, setShowJoinRequestModal] = useState(false);
  const [joinRequestPresenterId, setJoinRequestPresenterId] = useState<number | null>(null);
  const [presenterTracksEnabled, setPresenterTracksEnabled] = useState(false);
  const [removedFromLive, setRemovedFromLive] = useState(false);
  const [startButtonDisabledByOtherAdmin, setStartButtonDisabledByOtherAdmin] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(true);
  const [pretestDevices, setPretestDevices] = useState<MediaDeviceSelection | null>(null);
  const [remainingSessionSeconds, setRemainingSessionSeconds] = useState<number | null>(null);
  const [liveSettings, setLiveSettings] = useState<{
    displayParticipantsMinThreshold: number;
    chatMode: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
    displayParticipantsCountEnabled: boolean;
  }>({
    displayParticipantsMinThreshold: 0,
    chatMode: 'PUBLIC',
    displayParticipantsCountEnabled: true,
  });
  const [evergreenChatModeUpdating, setEvergreenChatModeUpdating] = useState(false);

  useEffect(() => {
    const room = liveKitRoomInstance;
    if (!room) return;

    let cleanupMediaStreamEnded: (() => void) | undefined;

    const clearScreenShareUi = () => {
      setIsScreenShareLive(false);
      setActiveShareScreen(false);
    };

    const attachScreenShareTrackEnded = () => {
      cleanupMediaStreamEnded?.();
      cleanupMediaStreamEnded = undefined;
      const pub = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
      const videoTrack = pub?.track;
      if (
        videoTrack &&
        'mediaStreamTrack' in videoTrack &&
        videoTrack.mediaStreamTrack &&
        videoTrack.mediaStreamTrack.readyState !== 'ended'
      ) {
        const mst = videoTrack.mediaStreamTrack;
        const onEnded = () => {
          clearScreenShareUi();
          // Arrêt depuis le bandeau natif du navigateur : volontaire, donc pas de reprise à proposer.
          if (sessionId) {
            clearScreenShareIntent(sessionId);
          }
        };
        mst.addEventListener('ended', onEnded);
        cleanupMediaStreamEnded = () => mst.removeEventListener('ended', onEnded);
      }
    };

    const onLocalTrackUnpublished = (publication: LocalTrackPublication) => {
      if (publication.source === Track.Source.ScreenShare) {
        cleanupMediaStreamEnded?.();
        cleanupMediaStreamEnded = undefined;
        clearScreenShareUi();
        // Arrêt depuis le bandeau natif : LiveKit dépublie, ce qui retire l'écouteur `ended` avant
        // qu'il ne serve. Toute dépublication hors fermeture de page vaut donc arrêt voulu.
        if (sessionId && !isUnloadingRef.current) {
          clearScreenShareIntent(sessionId);
        }
      }
    };

    const onLocalTrackPublished = (publication: LocalTrackPublication) => {
      if (publication.source === Track.Source.ScreenShare) {
        attachScreenShareTrackEnded();
      }
    };

    room.on(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
    room.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
    attachScreenShareTrackEnded();

    return () => {
      room.off(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
      room.off(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
      cleanupMediaStreamEnded?.();
    };
  }, [liveKitRoomInstance, sessionId]);

  // Le partage d'écran ne survit ni à un rechargement ni à une coupure : le navigateur exige un
  // nouveau geste utilisateur pour relancer une capture. On compare donc l'intention mémorisée à ce
  // qui est réellement publié, et on propose la reprise plutôt que de laisser la scène muette.
  useEffect(() => {
    const room = liveKitRoomInstance;
    if (!room || !sessionId) return;

    const offerResumeIfInterrupted = () => {
      if (room.state !== 'connected') return;
      // Ne rien proposer tant que la reprise échouerait : au retour d'un rechargement, le jeton
      // présentateur et le contrôle de partage arrivent après la connexion de la room.
      if (!presenterToken?.token || !screenShareControlRef.current) return;
      const isPublishing = screenShareControlRef.current?.isScreenSharing() ?? false;
      if (isPublishing) {
        // Le partage tourne : une interruption ultérieure devra pouvoir reproposer la reprise.
        screenShareResumeOfferedRef.current = false;
        return;
      }
      if (
        !shouldOfferScreenShareResume({
          intentAt: getScreenShareIntentAt(sessionId),
          now: Date.now(),
          isPublishing,
        })
      ) {
        return;
      }
      if (screenShareResumeOfferedRef.current) return;
      screenShareResumeOfferedRef.current = true;
      // Renoncer (croix du toast, ou sélecteur annulé) fait oublier l'intention : plus de relance.
      let resumeRequested = false;
      const toastId = toast.warning(
        <div className="flex flex-col gap-2">
          <span>{t('live.screenShareInterrupted')}</span>
          <Button
            type="button"
            className="h-10 w-fit"
            onClick={() => {
              resumeRequested = true;
              // Fermé avant d'ouvrir le sélecteur natif, sinon il s'affiche derrière le toast.
              toast.dismiss(toastId);
              void startScreenShareFlow().finally(() => {
                // Sélecteur annulé ou refusé : le présentateur a tranché, on ne repropose plus.
                if (!screenShareControlRef.current?.isScreenSharing()) {
                  clearScreenShareIntent(sessionId);
                }
              });
            }}
          >
            {t('live.screenShareResume')}
          </Button>
        </div>,
        {
          // Le présentateur doit pouvoir continuer à parler : jamais bloquant, mais jamais escamoté.
          autoClose: false,
          closeOnClick: false,
          onClose: () => {
            if (!resumeRequested) {
              clearScreenShareIntent(sessionId);
            }
          },
        },
      );
    };

    offerResumeIfInterrupted();
    room.on(RoomEvent.Connected, offerResumeIfInterrupted);
    room.on(RoomEvent.Reconnected, offerResumeIfInterrupted);

    return () => {
      room.off(RoomEvent.Connected, offerResumeIfInterrupted);
      room.off(RoomEvent.Reconnected, offerResumeIfInterrupted);
    };
  }, [liveKitRoomInstance, sessionId, presenterToken?.token]);

  useEffect(() => {
    if (currentWebinar) {
      setLiveSettings((prev) => ({
        ...prev,
        displayParticipantsMinThreshold: currentWebinar.displayParticipantsMinThreshold ?? 0,
        displayParticipantsCountEnabled: currentWebinar.displayParticipantsCountEnabled ?? true,
        chatMode: currentWebinar.chatMode ?? 'PUBLIC',
      }));
    }
  }, [currentWebinar]);

  const evergreenSessionIdentifier =
    currentWebinarSession?.sessionType === 'EVERGREEN' ? currentWebinarSession.identifier : null;

  // A replayed change missed while the tab was frozen is caught up when it becomes visible again.
  useEffect(() => {
    if (webinarId == null || !evergreenSessionIdentifier) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      dispatch(
        refreshEvergreenChatMode({ webinarId, sessionIdentifier: evergreenSessionIdentifier }),
      );
    };
    refresh();
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [webinarId, evergreenSessionIdentifier, dispatch]);

  // The mode is only shown once the server has applied it, so a failed attempt leaves the real one displayed.
  const handleEvergreenChatModeChange = async (chatMode: LiveChatMode) => {
    const sessionIdentifier = currentWebinarSession?.identifier;
    if (!workspaceId || !webinarId || !sessionId || !sessionIdentifier) return;
    setEvergreenChatModeUpdating(true);
    try {
      const confirmed = await liveService.updateEvergreenSessionChatMode(
        workspaceId,
        webinarId,
        sessionId,
        chatMode,
      );
      dispatch(
        applyEvergreenChatModeChange({
          sessionIdentifier,
          chatMode: confirmed.chatMode,
          version: readChatModeVersion(confirmed.origin, confirmed.revision),
        }),
      );
    } catch {
      toast.error(t('adminLive.chatModeUpdateFailed'));
    } finally {
      setEvergreenChatModeUpdating(false);
    }
  };

  const handleLiveSettingsChange = async (newSettings: {
    displayParticipantsMinThreshold: number;
    chatMode: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
    displayParticipantsCountEnabled: boolean;
  }) => {
    setLiveSettings(newSettings);
    if (
      workspaceId &&
      webinarId &&
      currentWebinar &&
      (newSettings.displayParticipantsMinThreshold !==
        currentWebinar.displayParticipantsMinThreshold ||
        newSettings.chatMode !== currentWebinar.chatMode ||
        newSettings.displayParticipantsCountEnabled !==
          currentWebinar.displayParticipantsCountEnabled)
    ) {
      try {
        await dispatch(
          updateWebinar({
            workspaceId,
            id: webinarId,
            input: {
              title: currentWebinar.title,
              shortDescription: currentWebinar.shortDescription,
              description: currentWebinar.description,
              displayParticipantsMinThreshold: newSettings.displayParticipantsMinThreshold,
              chatMode: newSettings.chatMode,
              displayParticipantsCountEnabled: newSettings.displayParticipantsCountEnabled,
            },
          }),
        ).unwrap();

        const configPayload = {
          chatMode: newSettings.chatMode,
          displayParticipantsMinThreshold: newSettings.displayParticipantsMinThreshold,
          displayParticipantsCountEnabled: newSettings.displayParticipantsCountEnabled,
        };

        if (channelName?.trim()) {
          try {
            await ably.channels.get(channelName).publish('live-event', {
              eventType: 'WEBINAR_CONFIG_UPDATED',
              resourceType: 'WEBINAR',
              resourceId: webinarId,
              payloadJson: JSON.stringify(configPayload),
            });
          } catch (err) {
            console.error('Ably WEBINAR_CONFIG_UPDATED (live-event) publish failed:', err);
          }
        }

        if (currentWebinarSession?.identifier && sessionId && workspaceId && webinarId) {
          try {
            await dispatch(
              triggerAdminEvent({
                workspaceId,
                webinarId,
                sessionIdentifier: currentWebinarSession.identifier,
                input: {
                  eventType: 'WEBINAR_CONFIG_UPDATED',
                  resourceType: 'WEBINAR',
                  resourceId: webinarId,
                  payloadJson: JSON.stringify(configPayload),
                },
              }),
            ).unwrap();
          } catch (err) {
            console.error('triggerAdminEvent WEBINAR_CONFIG_UPDATED failed:', err);
          }
        }
      } catch {
        setLiveSettings((prev) => ({
          ...prev,
          displayParticipantsMinThreshold: currentWebinar.displayParticipantsMinThreshold ?? 0,
          chatMode: currentWebinar.chatMode ?? 'PUBLIC',
          displayParticipantsCountEnabled: currentWebinar.displayParticipantsCountEnabled ?? true,
        }));
      }
    }
  };

  useEffect(() => {
    const compute = () => {
      const startedAt = currentWebinarSession?.startedAt;
      const sessionDuration = usage?.sessionDuration;
      if (!startedAt || !sessionDuration?.max) {
        setRemainingSessionSeconds(null);
        return;
      }
      const startUtc = DateTime.fromISO(startedAt, { zone: 'utc' });
      const nowUtc = DateTime.utc();
      const elapsedSeconds = Math.floor(nowUtc.diff(startUtc, 'seconds').seconds);
      const maxSeconds = (sessionDuration.max - sessionDuration.current) * 60;
      const remaining = Math.max(0, maxSeconds - elapsedSeconds);
      setRemainingSessionSeconds(remaining);
    };
    compute();
    const interval = setInterval(compute, 1000);
    return () => clearInterval(interval);
  }, [currentWebinarSession?.startedAt, usage?.sessionDuration]);

  const formatRemainingTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  /**
   * F5 + admin (une fois) : en UPCOMING, chaque ressource encore `live` reçoit RESOURCE_HIDE
   * (même sémantique que le masquage dans l’UI), puis layout grille + re-fetch état.
   */
  const didPostReloadMediaResetRef = useRef(false);

  useEffect(() => {
    if (!workspaceId || !webinarId || !sessionId) {
      return;
    }
    if (adminChatRole == null) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const needsSessionForReloadGate =
        adminChatRole === 'admin' &&
        isDocumentReloadNavigation() &&
        !didPostReloadMediaResetRef.current;

      let didFetchAdminResourceInReloadGate = false;

      if (needsSessionForReloadGate) {
        const sessionResult = await dispatch(
          getWebinarSession({ workspaceId, webinarId, sessionId }),
        );
        if (cancelled) {
          return;
        }
        const resourceStateResult = await dispatch(
          fetchAdminResourceState({ workspaceId, webinarId, sessionId }),
        );
        if (fetchAdminResourceState.fulfilled.match(resourceStateResult)) {
          didFetchAdminResourceInReloadGate = true;
        }
        if (cancelled) {
          return;
        }

        const sessionOk = getWebinarSession.fulfilled.match(sessionResult)
          ? sessionResult.payload
          : null;
        const resourceStates = fetchAdminResourceState.fulfilled.match(resourceStateResult)
          ? resourceStateResult.payload
          : null;

        const runUpcomingReloadHideAllLive = async () => {
          if (!sessionOk?.identifier) {
            return;
          }
          const sessionIdentifier = sessionOk.identifier;
          const timePayload = JSON.stringify({ timeNow: Date.now() });
          for (const s of (resourceStates ?? []).filter((st) => st.live)) {
            if (cancelled) {
              return;
            }
            try {
              await dispatch(
                triggerAdminEvent({
                  workspaceId,
                  webinarId,
                  sessionIdentifier,
                  input: {
                    eventType: 'RESOURCE_HIDE',
                    resourceType: s.resourceType,
                    resourceId: s.resourceId,
                    payloadJson: timePayload,
                  },
                }),
              ).unwrap();
            } catch (err) {
              console.error('RESOURCE_HIDE (refresh UPCOMING):', err);
            }
          }
          if (cancelled) {
            return;
          }
          clearStoredAdminPdfPagesForSession(sessionId);
          clearScreenShareIntent(sessionId);
          if (cancelled) {
            return;
          }
          await dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
        };

        if (!sessionOk) {
          didPostReloadMediaResetRef.current = true;
        } else if (sessionOk.status === 'UPCOMING') {
          await runUpcomingReloadHideAllLive();
          if (!cancelled) {
            didPostReloadMediaResetRef.current = true;
          }
        } else {
          didPostReloadMediaResetRef.current = true;
        }
      } else {
        void dispatch(getWebinarSession({ workspaceId, webinarId, sessionId }));
      }

      if (cancelled) {
        return;
      }

      void dispatch(fetchWebinarById({ workspaceId, id: webinarId }));
      void dispatch(listWebinarSlides({ workspaceId, webinarId }));
      void dispatch(listWebinarResources({ workspaceId, webinarId, page: 0, size: 30 }));
      void dispatch(listSurveyTemplates({ workspaceId, webinarId }));
      void dispatch(listQuizTemplates({ workspaceId, webinarId }));
      void dispatch(listOfferTemplates({ workspaceId, webinarId }));
      void dispatch(listSpeakers({ workspaceId, webinarId }));
      void dispatch(fetchPlayback({ workspaceId, webinarId, sessionId }));
      void dispatch(listPresenters({ workspaceId, webinarId }));
      void dispatch(fetchUsage(workspaceId));

      const sessionType = currentWebinarSession?.sessionType;

      if (shouldRequestModeratorLiveKitToken(sessionType, adminChatRole)) {
        void dispatch(fetchModeratorToken({ workspaceId, webinarId, sessionId }));
      }
      if (adminChatRole === 'admin' && sessionType != null && sessionType !== 'EVERGREEN') {
        void dispatch(fetchPresenterToken({ workspaceId, webinarId, sessionId }));
      }
      if (!didFetchAdminResourceInReloadGate) {
        void dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    workspaceId,
    webinarId,
    sessionId,
    adminChatRole,
    currentWebinarSession?.sessionType,
  ]);

  const recordedStaffPresenceSessionRef = useRef<number | null>(null);

  // Un seul POST par session : la contrainte d'unicité back rejetterait deux appels concurrents.
  useEffect(() => {
    if (!workspaceId || !webinarId || !sessionId) {
      return;
    }
    if (
      !shouldRecordStaffPresence(
        currentWebinarSession?.sessionType,
        adminChatRole,
        currentWebinarSession?.status,
      )
    ) {
      return;
    }
    if (recordedStaffPresenceSessionRef.current === sessionId) {
      return;
    }
    recordedStaffPresenceSessionRef.current = sessionId;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const postPresence = (retriesLeft: number) => {
      void liveService.recordStaffPresence(workspaceId, webinarId, sessionId).catch((error) => {
        if (cancelled) {
          return;
        }
        if (retriesLeft > 0 && shouldRetryStaffPresence(error)) {
          retryTimer = setTimeout(
            () => postPresence(retriesLeft - 1),
            STAFF_PRESENCE_RETRY_DELAY_MS,
          );
          return;
        }
        recordedStaffPresenceSessionRef.current = null;
      });
    };

    postPresence(STAFF_PRESENCE_MAX_RETRIES);

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [
    workspaceId,
    webinarId,
    sessionId,
    adminChatRole,
    currentWebinarSession?.sessionType,
    currentWebinarSession?.status,
  ]);

  useEffect(() => {
    if (!workspaceId) return;
    void dispatch(fetchUsage(workspaceId));
    const interval = setInterval(() => {
      void dispatch(fetchUsage(workspaceId));
    }, 5000);
    return () => clearInterval(interval);
  }, [dispatch, workspaceId]);

  useEffect(() => {
    if (usage?.participants?.current !== undefined) {
      setLiveParticipantCount(usage.participants.current);
    }
  }, [usage?.participants?.current]);

  const surveys: SurveyTemplate[] =
    surveyTemplates?.filter((survey) => survey.waitingRoomSurvey !== true) || [];
  const quizs = quizTemplates?.filter((quiz) => quiz.waitingRoomQuiz !== true) || [];
  const offers: OfferTemplate[] =
    offerTemplates?.filter((offer) => offer.thankYouPageOffer !== true) || [];

  const videos: WebinarResource[] =
    webinarResources?.filter(
      (resource) =>
        resource.mimeType === 'video/mp4' ||
        resource.mimeType === 'video/quicktime' ||
        resource.mimeType === 'video/webm' ||
        resource.mimeType === 'video/ogg' ||
        resource.mimeType === 'video/avi' ||
        resource.mimeType === 'video/mov' ||
        resource.mimeType === 'video/wmv' ||
        resource.mimeType === 'video/flv' ||
        resource.mimeType === 'video/mkv' ||
        resource.mimeType === 'video/mpg' ||
        resource.mimeType === 'video/mpeg' ||
        resource.mimeType === 'video/m4v' ||
        resource.mimeType === 'video/m4p',
    ) || [];

  useEffect(() => {
    if (currentWebinarSession?.status === 'UPCOMING') {
      setCurrentStep(1);
      setPreliveLive(true);
    }
  }, [currentWebinarSession?.status]);
  const formattedPlannedDate = useMemo(
    () => formatPlannedLiveDate(currentWebinarSession?.utcPlannedDate, i18n.language),
    [currentWebinarSession?.utcPlannedDate, i18n.language],
  );
  useChannel(channelName || '', (message) => {
    if (!channelName || !message) return;

    const normalizedMessages = normalizeLiveBatchMessage(message);

    for (const currentMessage of normalizedMessages) {
      // Un lot republié par le buffer Redis du back ne doit pas rejouer l'event côté régie.
      if (!liveEventDeduplicatorRef.current.shouldProcess(currentMessage.eventId)) {
        continue;
      }
      const data: any = currentMessage.data;

      if (currentMessage.name === 'presence-update') {
        const live = parsePresenceUpdateLiveCount(data);
        if (live !== null) {
          setLiveParticipantCount(live);
        }
      }
      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.resourceType === 'SHARED_RESOURCE' &&
        (data.eventType === 'RESOURCE_SHOW' || data.eventType === 'RESOURCE_HIDE')
      ) {
        const numericResourceId =
          typeof data.resourceId === 'number' && Number.isFinite(data.resourceId)
            ? data.resourceId
            : typeof data.resourceId === 'string' && /^\d+$/.test(data.resourceId.trim())
              ? Number.parseInt(data.resourceId.trim(), 10)
              : null;

        if (numericResourceId != null) {
          if (data.eventType === 'RESOURCE_SHOW') {
            try {
              const parsedPayload = data.payloadJson
                ? typeof data.payloadJson === 'string'
                  ? JSON.parse(data.payloadJson)
                  : data.payloadJson
                : null;
              const resource = parsedPayload?.resource;
              if (resource && typeof resource.id === 'number') {
                dispatch(applySharedResourceLiveEvent({ type: 'SHARED_RESOURCE_SHOW', resource }));
              }
            } catch {
              // payload malformé : rien à afficher, pas de crash
            }
          } else {
            dispatch(
              applySharedResourceLiveEvent({
                type: 'SHARED_RESOURCE_HIDE',
                resource: { id: numericResourceId },
              }),
            );
          }
        }
      }

      if (currentMessage.name === 'live-reaction') {
        if (data && typeof data === 'object' && data.text) {
          console.log('🎉 Live Reaction reçue:', data.text);

          const reactionId = crypto.randomUUID();
          const randomLeft = Math.random() * 80 + 10;
          setCurrentReactions((prev) => [
            ...prev,
            { id: reactionId, text: data.text, left: randomLeft },
          ]);

          setTimeout(() => {
            setCurrentReactions((prev) => prev.filter((reaction) => reaction.id !== reactionId));
          }, 3000);
        }
      }

      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.eventType === 'PRESENTER_JOIN_REQUEST' &&
        data.resourceType === 'PRESENTER' &&
        user?.id
      ) {
        let presenterUserId: string | number | undefined;
        try {
          const payload = data.payloadJson
            ? typeof data.payloadJson === 'string'
              ? JSON.parse(data.payloadJson)
              : data.payloadJson
            : {};
          presenterUserId = payload.presenterUserId;
        } catch {
          presenterUserId = undefined;
        }
        if (presenterUserId != null && String(presenterUserId) === String(user.id)) {
          setJoinRequestPresenterId(typeof data.resourceId === 'number' ? data.resourceId : null);
          setShowJoinRequestModal(true);
        }
      }

      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.eventType === 'PRESENTER_REMOVE_REQUEST' &&
        data.resourceType === 'PRESENTER' &&
        user?.id
      ) {
        let presenterUserId: string | number | undefined;
        try {
          const payload = data.payloadJson
            ? typeof data.payloadJson === 'string'
              ? JSON.parse(data.payloadJson)
              : data.payloadJson
            : {};
          presenterUserId = payload.presenterUserId;
        } catch {
          presenterUserId = undefined;
        }
        if (presenterUserId != null && String(presenterUserId) === String(user.id)) {
          setRemovedFromLive(true);
        }
      }

      const isStartButtonDisabledPayload =
        (currentMessage.name === 'live-event' &&
          data &&
          typeof data === 'object' &&
          data.eventType === 'START_BUTTON_DISABLED') ||
        (currentMessage.name === 'event-message' &&
          data &&
          typeof data === 'object' &&
          data.type === 'START_BUTTON_DISABLED');

      const startButtonDisabledAppliesToThisSession =
        !data ||
        typeof data !== 'object' ||
        data.resourceType !== 'SESSION' ||
        data.resourceId == null ||
        sessionId == null ||
        Number(data.resourceId) === sessionId;

      if (isStartButtonDisabledPayload && startButtonDisabledAppliesToThisSession) {
        setStartButtonDisabledByOtherAdmin(true);
      }

      const shouldRefetchSessionFromChannel =
        workspaceId &&
        webinarId &&
        sessionId &&
        data &&
        typeof data === 'object' &&
        ((currentMessage.name === 'live-event' && data.eventType === 'SESSION_UPDATED') ||
          (currentMessage.name === 'event-message' && data.type === 'SESSION_UPDATE'));

      if (shouldRefetchSessionFromChannel) {
        setStartButtonDisabledByOtherAdmin(false);
        void dispatch(getWebinarSession({ workspaceId, webinarId, sessionId }));
        void dispatch(fetchPlayback({ workspaceId, webinarId, sessionId }));
      }

      if (typeof data.streamStartStatus === 'string') {
        dispatch(setStreamStartStatus(data.streamStartStatus));
      }

      if (
        isSessionDurationExceeded(currentMessage.name, data) &&
        !durationExceededShownRef.current
      ) {
        durationExceededShownRef.current = true;
        toast.warning(<LiveDurationToast />, { autoClose: false });
      }

      const chatModeChange = readChatModeChange(currentMessage.name, data);
      if (chatModeChange && currentWebinarSession?.identifier) {
        dispatch(
          applyEvergreenChatModeChange({
            sessionIdentifier: currentWebinarSession.identifier,
            ...chatModeChange,
          }),
        );
      }

      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.eventType === 'WEBINAR_CONFIG_UPDATED' &&
        data.resourceType === 'WEBINAR' &&
        data.payloadJson &&
        workspaceId &&
        webinarId != null &&
        Number(data.resourceId) === webinarId
      ) {
        try {
          const payload =
            typeof data.payloadJson === 'string' ? JSON.parse(data.payloadJson) : data.payloadJson;
          if (payload && typeof payload === 'object') {
            const p = payload as {
              displayParticipantsMinThreshold?: number;
              displayParticipantsCountEnabled?: boolean;
              chatMode?: string;
            };
            setLiveSettings((prev) => ({
              displayParticipantsMinThreshold:
                typeof p.displayParticipantsMinThreshold === 'number'
                  ? p.displayParticipantsMinThreshold
                  : prev.displayParticipantsMinThreshold,
              displayParticipantsCountEnabled:
                typeof p.displayParticipantsCountEnabled === 'boolean'
                  ? p.displayParticipantsCountEnabled
                  : prev.displayParticipantsCountEnabled,
              chatMode:
                p.chatMode === 'PUBLIC' || p.chatMode === 'PRIVATE' || p.chatMode === 'DISABLED'
                  ? p.chatMode
                  : prev.chatMode,
            }));
            void dispatch(fetchWebinarById({ workspaceId, id: webinarId }));
          }
        } catch {
          console.error('payload invalide WEBINAR_CONFIG_UPDATED');
        }
      }

      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.eventType === 'SESSION_CLOSE'
      ) {
        navigate(`/analytics/sessions/${sessionId}`, {
          replace: true,
          state: { fromLiveClose: true },
        });
      }

      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.eventType === 'SESSION_CLOSING_IMMINENT' &&
        data.resourceType === 'SESSION' &&
        sessionId != null &&
        Number(data.resourceId) === sessionId
      ) {
        toast.warning(t('common.closeSessionDescription2Bold'));
      }

      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        (data.resourceType === 'WEBINAR_RESOURCE' ||
          data.resourceType === 'WEBINAR_SLIDE' ||
          data.resourceType === 'SURVEY_TEMPLATE' ||
          data.resourceType === 'QUIZ_TEMPLATE' ||
          data.resourceType === 'OFFER_TEMPLATE') &&
        (data.eventType === 'RESOURCE_SHOW' ||
          data.eventType === 'RESOURCE_HIDE' ||
          data.eventType === 'PDF_PAGE_CHANGE') &&
        workspaceId &&
        webinarId &&
        sessionId
      ) {
        void dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
        if (
          data.resourceType === 'SURVEY_TEMPLATE' &&
          data.eventType === 'RESOURCE_HIDE' &&
          typeof data.resourceId === 'number'
        ) {
          void dispatch(
            fetchSurveyResults({
              workspaceId,
              webinarId,
              templateId: data.resourceId,
              sessionId,
            }),
          );
        }
        if (
          data.resourceType === 'QUIZ_TEMPLATE' &&
          data.eventType === 'RESOURCE_HIDE' &&
          typeof data.resourceId === 'number'
        ) {
          void dispatch(
            fetchQuizResults({
              workspaceId,
              webinarId,
              templateId: data.resourceId,
              sessionId,
            }),
          );
        }
        if (
          data.resourceType === 'OFFER_TEMPLATE' &&
          data.eventType === 'RESOURCE_HIDE' &&
          typeof data.resourceId === 'number'
        ) {
          void dispatch(
            fetchOfferResults({
              workspaceId,
              webinarId,
              offerId: data.resourceId,
              sessionId,
            }),
          );
        }
      }

      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.eventType === 'RESOURCE_UPDATE' &&
        workspaceId &&
        webinarId
      ) {
        if (data.resourceType === 'SURVEY_TEMPLATE') {
          void dispatch(listSurveyTemplates({ workspaceId, webinarId }));
        } else if (data.resourceType === 'QUIZ_TEMPLATE') {
          void dispatch(listQuizTemplates({ workspaceId, webinarId }));
        } else if (data.resourceType === 'OFFER_TEMPLATE') {
          void dispatch(listOfferTemplates({ workspaceId, webinarId }));
        }
      }
    }
  });

  const handleItemActivation = (itemType: 'presenters' | 'videos' | 'slides' | 'shareScreen') => {
    if (
      (itemType === 'presenters' && activePresenters) ||
      (itemType === 'videos' && activeVideos) ||
      (itemType === 'slides' && activeSlides) ||
      (itemType === 'shareScreen' && activeShareScreen)
    ) {
      setActivePresenters(false);
      setActiveVideos(false);
      setActiveSlides(false);
      setActiveShareScreen(false);
      return;
    }

    setActivePresenters(false);
    setActiveVideos(false);
    setActiveSlides(false);
    setActiveShareScreen(false);
    switch (itemType) {
      case 'presenters':
        setActivePresenters(true);
        break;
      case 'videos':
        setActiveVideos(true);
        if (workspaceId != null && webinarId != null) {
          void dispatch(listWebinarResources({ workspaceId, webinarId, page: 0, size: 30 }));
        }
        break;
      case 'slides':
        setActiveSlides(true);
        if (workspaceId != null && webinarId != null) {
          void dispatch(listWebinarSlides({ workspaceId, webinarId }));
        }
        break;
      case 'shareScreen':
        setActiveShareScreen(true);
        break;
    }
  };

  const handleAcceptJoinRequest = () => {
    setRemovedFromLive(false);
    setPresenterTracksEnabled(true);
    setShowJoinRequestModal(false);
  };

  const handleRejectJoinRequest = () => {
    setShowJoinRequestModal(false);
    if (!currentWebinarSession?.identifier || !workspaceId || !webinarId || !user?.id) return;
    if (joinRequestPresenterId == null) return;
    void dispatch(
      triggerAdminEvent({
        workspaceId,
        webinarId,
        sessionIdentifier: currentWebinarSession.identifier,
        input: {
          eventType: 'PRESENTER_JOIN_DECLINED',
          resourceType: 'PRESENTER',
          resourceId: joinRequestPresenterId,
          payloadJson: JSON.stringify({ presenterUserId: user.id }),
        },
      }),
    );
  };

  const handleIconToggle = (iconType: 'like' | 'list' | 'question' | 'folder' | 'gift') => {
    setActiveLike(iconType === 'like' ? !activeLike : false);
    setActiveList(iconType === 'list' ? !activeList : false);
    setActiveQuestion(iconType === 'question' ? !activeQuestion : false);
    setActiveFolder(iconType === 'folder' ? !activeFolder : false);
    setActiveGift(iconType === 'gift' ? !activeGift : false);

    // Les panneaux restent montés : sans ce rechargement à l'ouverture, une création faite
    // ailleurs pendant le live n'apparaîtrait qu'après un rafraîchissement de la page.
    if (workspaceId == null || webinarId == null) return;
    if (iconType === 'list' && !activeList) {
      void dispatch(listSurveyTemplates({ workspaceId, webinarId }));
    }
    if (iconType === 'question' && !activeQuestion) {
      void dispatch(listQuizTemplates({ workspaceId, webinarId }));
    }
    if (iconType === 'gift' && !activeGift) {
      void dispatch(listOfferTemplates({ workspaceId, webinarId }));
    }
  };

  const reapplyLiveResourcesAfterRecordingStart = async (
    sessionIdentifier: string,
    resourceStates: LiveSessionResourceStateDto[],
    slideHint: LiveSessionResourceStateDto | null,
  ) => {
    if (!workspaceId || !webinarId || !sessionId) {
      return;
    }

    const live = resourceStates.filter((s) => s.live);
    const activeSlideState = pickSlideStateForReapply(resourceStates, slideHint);
    const videoState = live.find((s) => s.resourceType === 'WEBINAR_RESOURCE');

    if (live.length === 0 && !activeSlideState && !videoState) {
      return;
    }

    if (activeSlideState || videoState) {
      try {
        await screenShareControlRef.current?.stopScreenShare();
      } catch (e) {
        console.error("Arrêt du partage d'écran (reprise d'enregistrement / relance PDF–vidéo)", e);
      }
      setIsScreenShareLive(false);
      setActiveShareScreen(false);
      clearScreenShareIntent(sessionId);
    }

    const pushLiveKitLayout = async (input: LiveKitLayoutInput) => {
      try {
        await dispatch(updateSessionLayout({ workspaceId, webinarId, sessionId, input })).unwrap();
      } catch (e) {
        console.error(
          "Échec updateSessionLayout (métadonnées LiveKit / viewer) — vérifier les droits (ex. modérateur) sur l'API layout.",
          e,
        );
      }
    };

    const findSlideInStore = (resourceId: number) =>
      store
        .getState()
        .webinar.webinarSlides?.find((s) => s.id === resourceId || s.id === Number(resourceId));
    const findResourceInStore = (resourceId: number) =>
      store
        .getState()
        .webinar.webinarResources?.find((r) => r.id === resourceId || r.id === Number(resourceId));

    if (activeSlideState) {
      let slide = findSlideInStore(activeSlideState.resourceId);
      if (!slide?.slideUrl) {
        try {
          await dispatch(listWebinarSlides({ workspaceId, webinarId })).unwrap();
        } catch {
          /* slides déjà en cours ou erreur : on tente le store quand même */
        }
        slide = findSlideInStore(activeSlideState.resourceId);
      }
      if (slide?.slideUrl) {
        const pageFromServer = activeSlideState.lastStatePayload?.pageNumber;
        const page =
          getStoredAdminPdfPage(sessionId, activeSlideState.resourceId) ??
          (typeof pageFromServer === 'number' && pageFromServer >= 1 ? pageFromServer : 1);
        await dispatch(
          triggerAdminEvent({
            workspaceId,
            webinarId,
            sessionIdentifier,
            input: {
              eventType: 'RESOURCE_HIDE',
              resourceType: 'WEBINAR_SLIDE',
              resourceId: activeSlideState.resourceId,
              payloadJson: JSON.stringify({ timeNow: Date.now() }),
            },
          }),
        );
        await pushLiveKitLayout({ layout: 'grid', activeAsset: null });
        const slideShow = await dispatch(
          triggerAdminEvent({
            workspaceId,
            webinarId,
            sessionIdentifier,
            input: {
              eventType: 'RESOURCE_SHOW',
              resourceType: 'WEBINAR_SLIDE',
              resourceId: activeSlideState.resourceId,
              payloadJson: JSON.stringify({ timeNow: Date.now(), pageNumber: page }),
            },
          }),
        );
        if (!triggerAdminEvent.fulfilled.match(slideShow)) {
          console.warn(
            'RESOURCE_SHOW (WEBINAR_SLIDE) non satisfait après reprise enregistrement — le layout est poussé quand même pour le viewer / robot',
            slideShow,
          );
        }
        await pushLiveKitLayout({
          layout: 'presentation',
          activeAsset: { type: 'PDF', url: slide.slideUrl, page },
        });
      }
    } else if (videoState) {
      let videoResource = findResourceInStore(videoState.resourceId);
      if (!videoResource?.resourceUrl) {
        try {
          await dispatch(
            listWebinarResources({ workspaceId, webinarId, page: 0, size: 30 }),
          ).unwrap();
        } catch {
          /* idem */
        }
        videoResource = findResourceInStore(videoState.resourceId);
      }
      if (videoResource?.resourceUrl) {
        const videoShow = await dispatch(
          triggerAdminEvent({
            workspaceId,
            webinarId,
            sessionIdentifier,
            input: {
              eventType: 'RESOURCE_SHOW',
              resourceType: 'WEBINAR_RESOURCE',
              resourceId: videoState.resourceId,
              payloadJson: JSON.stringify({ timeNow: Date.now() }),
            },
          }),
        );
        if (!triggerAdminEvent.fulfilled.match(videoShow)) {
          console.warn(
            'RESOURCE_SHOW (WEBINAR_RESOURCE) non satisfait après reprise enregistrement — le layout est poussé quand même',
            videoShow,
          );
        }
        // L'état de lecture est lu au moment de la reprise : sans lui le compositor laisse la vidéo en pause.
        await pushLiveKitLayout({
          layout: 'presentation',
          activeAsset: buildVideoActiveAsset(
            videoResource.resourceUrl,
            videoPlayerStateRef.current?.getVideoLayoutState(),
          ),
        });
      }
    }

    const templateTypes = new Set(['SURVEY_TEMPLATE', 'QUIZ_TEMPLATE', 'OFFER_TEMPLATE']);
    for (const s of live) {
      if (!templateTypes.has(s.resourceType)) {
        continue;
      }
      await dispatch(
        triggerAdminEvent({
          workspaceId,
          webinarId,
          sessionIdentifier,
          input: {
            eventType: 'RESOURCE_SHOW',
            resourceType: s.resourceType,
            resourceId: s.resourceId,
            payloadJson: JSON.stringify({ timeNow: Date.now() }),
          },
        }),
      );
    }

    await dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
  };

  const handleStartSession = async () => {
    if (!workspaceId || !webinarId || !sessionId) {
      return;
    }
    // Filet de sécurité : un compte non payant ne peut pas lancer de live. Le bouton
    // est remplacé en amont par le CTA d'upgrade, donc on ne devrait pas arriver ici ;
    // si c'est le cas on bloque sans tenter un démarrage voué au 402.
    if (isFreeWithoutLiveAccess) {
      return;
    }
    if (currentWebinarSession?.identifier) {
      await dispatch(
        triggerAdminEvent({
          workspaceId,
          webinarId,
          sessionIdentifier: currentWebinarSession.identifier,
          input: {
            eventType: 'START_BUTTON_DISABLED',
            resourceType: 'SESSION',
            resourceId: sessionId,
          },
        }),
      );
    }
    setStartButtonDisabledByOtherAdmin(true);

    const slideAtLaunch = captureSlideHintFromStore(store.getState().live.adminResourceStates);

    const result = await dispatch(startCustomRecording({ workspaceId, webinarId, sessionId }));

    if (startCustomRecording.fulfilled.match(result)) {
      const sessionResult = await dispatch(
        getWebinarSession({ workspaceId, webinarId, sessionId }),
      );

      if (getWebinarSession.fulfilled.match(sessionResult) && sessionResult.payload?.identifier) {
        const sessionIdentifier = sessionResult.payload.identifier;
        await dispatch(
          triggerAdminEvent({
            workspaceId,
            webinarId,
            sessionIdentifier,
            input: {
              eventType: 'SESSION_UPDATED',
              resourceType: 'SESSION',
              resourceId: sessionId,
            },
          }),
        );

        const refreshedResources = await dispatch(
          fetchAdminResourceState({ workspaceId, webinarId, sessionId }),
        );
        if (fetchAdminResourceState.fulfilled.match(refreshedResources)) {
          const payload = refreshedResources.payload;
          const slideHint = slideAtLaunch ?? captureSlideHintFromStore(payload);
          await new Promise((r) => setTimeout(r, 400));
          await reapplyLiveResourcesAfterRecordingStart(sessionIdentifier, payload, slideHint);
        }
      }
    } else {
      // Lancement sécurisé KO (424 : un egress n'est pas devenu actif dans le délai, ou autre erreur).
      // On recharge le bouton pour permettre une relance immédiate et on prévient l'admin.
      setStartButtonDisabledByOtherAdmin(false);
      if (isYouTube) {
        const isNotLive = result.payload?.message?.includes('YOUTUBE.NOT_LIVE');
        toast.error(t(isNotLive ? 'live.youtube.notLive' : 'live.youtube.startFailed'));
        return;
      }
      const isEgressFailure = result.payload?.message?.includes('CUSTOM_EGRESS_NOT_ACTIVE');
      toast.error(
        t(
          isEgressFailure
            ? 'liveSettings.startSessionErrorEgress'
            : 'liveSettings.startSessionErrorGeneric',
        ),
      );
      return;
    }

    // Le live repart d'une base saine : on aligne la mémoire du partage sur ce qui est réellement
    // publié, ce qui purge une trace résiduelle sans casser un partage déjà en cours au lancement.
    reconcileScreenShareIntent(
      sessionId,
      screenShareControlRef.current?.isScreenSharing() ?? false,
      Date.now(),
    );

    await Promise.all([dispatch(fetchPlayback({ workspaceId, webinarId, sessionId }))]);
    globalThis.setTimeout(() => {
      void dispatch(fetchPlayback({ workspaceId, webinarId, sessionId }));
    }, 2500);
  };

  const handleCloseSession = async () => {
    if (!workspaceId || !webinarId || !sessionId || !playbackInfo) {
      return;
    }

    const sessionIdentifier = currentWebinarSession?.identifier;
    if (!sessionIdentifier) {
      return;
    }

    const result = await dispatch(closeSession({ workspaceId, webinarId, sessionId }));

    if (closeSession.fulfilled.match(result)) {
      clearStoredAdminPdfPagesForSession(sessionId);
      clearScreenShareIntent(sessionId);
      await dispatch(
        triggerAdminEvent({
          workspaceId,
          webinarId,
          sessionIdentifier,
          input: {
            eventType: 'SESSION_CLOSE',
            resourceType: 'SESSION',
            resourceId: sessionId,
          },
        }),
      );

      navigate(`/analytics/sessions/${sessionId}`, {
        replace: true,
        state: { fromLiveClose: true },
      });
    }
  };

  const handleTriggerEvent = async (event: TriggerEventInput) => {
    if (!workspaceId || !webinarId || !sessionId || !currentWebinarSession?.identifier) {
      return;
    }

    const sessionIdentifier = currentWebinarSession.identifier;

    if (event.eventType === 'RESOURCE_SHOW') {
      if (event.resourceType === 'WEBINAR_SLIDE' || event.resourceType === 'WEBINAR_RESOURCE') {
        try {
          await screenShareControlRef.current?.stopScreenShare();
        } catch (e) {
          console.error("Arrêt du partage d'écran avant lancement PDF/vidéo", e);
        }
        setIsScreenShareLive(false);
        setActiveShareScreen(false);
        if (sessionId) {
          clearScreenShareIntent(sessionId);
        }
      }
      for (const state of resourcesToHideOnShow(adminResourceStates, event)) {
        const hideResult = await dispatch(
          triggerAdminEvent({
            workspaceId,
            webinarId,
            sessionIdentifier,
            input: {
              eventType: 'RESOURCE_HIDE',
              resourceType: state.resourceType,
              resourceId: state.resourceId,
              payloadJson: JSON.stringify({ timeNow: new Date().getTime() }),
            },
          }),
        );
        if (triggerAdminEvent.fulfilled.match(hideResult) && channelName) {
          await dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
        }
      }
      if (INTERACTION_RESOURCE_TYPES.includes(event.resourceType)) {
        const liveSharedResources = adminResourceStates.filter(
          (s) => s.resourceType === 'SHARED_RESOURCE' && s.live,
        );
        for (const state of liveSharedResources) {
          await dispatch(
            adminHideSharedResource({
              workspaceId,
              webinarId,
              sessionIdentifier,
              sharedResourceId: state.resourceId,
            }),
          );
        }
      }
    }

    const result = await dispatch(
      triggerAdminEvent({
        workspaceId,
        webinarId,
        sessionIdentifier,
        input: event,
      }),
    );

    if (triggerAdminEvent.fulfilled.match(result)) {
      const { webinarSlides: slidesFresh, webinarResources: resourcesFresh } =
        store.getState().webinar;

      const pushLiveKitLayout = async (input: LiveKitLayoutInput) => {
        try {
          await dispatch(
            updateSessionLayout({ workspaceId, webinarId, sessionId, input }),
          ).unwrap();
        } catch (e) {
          console.error(
            "Échec updateSessionLayout (métadonnées LiveKit / viewer) — vérifier les droits (ex. modérateur) sur l'API layout.",
            e,
          );
        }
      };

      const findSlideById = (resourceId: number) =>
        slidesFresh?.find((s) => s.id === resourceId || s.id === Number(resourceId));

      if (
        event.resourceType === 'WEBINAR_SLIDE' &&
        event.resourceId != null &&
        (event.eventType === 'PDF_PAGE_CHANGE' || event.eventType === 'RESOURCE_SHOW')
      ) {
        try {
          const p = event.payloadJson ? JSON.parse(event.payloadJson) : {};
          if (typeof p.pageNumber === 'number' && p.pageNumber >= 1) {
            setStoredAdminPdfPage(sessionId, event.resourceId, p.pageNumber);
          }
        } catch {
          console.error('Error parsing PDF page number', event.payloadJson);
        }
      }

      await dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));

      if (event.resourceType === 'WEBINAR_RESOURCE') {
        setActiveVideos(false);
        if (
          event.eventType === 'RESOURCE_SHOW' &&
          event.resourceId != null &&
          resourcesFresh?.length
        ) {
          const videoResource = resourcesFresh.find(
            (r) => r.id === event.resourceId || r.id === Number(event.resourceId),
          );
          if (videoResource?.resourceUrl) {
            await pushLiveKitLayout({
              layout: 'presentation',
              activeAsset: { type: 'VIDEO' as const, url: videoResource.resourceUrl },
            });
          }
        } else if (event.eventType === 'RESOURCE_HIDE') {
          await pushLiveKitLayout({ layout: 'grid', activeAsset: null });
        }
      }
      if (event.resourceType === 'WEBINAR_SLIDE') {
        setActiveSlides(false);
        const shouldSyncPdf =
          (event.eventType === 'RESOURCE_SHOW' || event.eventType === 'PDF_PAGE_CHANGE') &&
          event.resourceId != null &&
          Boolean(slidesFresh?.length);

        if (shouldSyncPdf) {
          const slide = findSlideById(event.resourceId);
          if (slide?.slideUrl) {
            const page = (() => {
              try {
                const p = event.payloadJson ? JSON.parse(event.payloadJson) : {};
                return p.pageNumber ?? 1;
              } catch {
                return 1;
              }
            })();
            await pushLiveKitLayout({
              layout: 'presentation',
              activeAsset: { type: 'PDF' as const, url: slide.slideUrl, page },
            });
          }
        } else if (event.eventType === 'RESOURCE_HIDE') {
          await pushLiveKitLayout({ layout: 'grid', activeAsset: null });
        }
      }
    }
  };

  const startScreenShareFlow = async () => {
    if (!presenterToken?.token) {
      toast.error(t('adminLive.joinRoomToShare'));
      return;
    }
    const ctl = screenShareControlRef.current;
    if (!ctl) {
      toast.error(t('adminLive.joinRoomToShare'));
      return;
    }
    try {
      await ctl.startScreenShare();

      if (!ctl.isScreenSharing()) {
        toast.error(t('live.screenShareStartFailed'));
        return;
      }

      const states = store.getState().live.adminResourceStates;
      const liveVideos = states.filter((s) => s.resourceType === 'WEBINAR_RESOURCE' && s.live);
      for (const state of liveVideos) {
        await handleTriggerEvent({
          eventType: 'RESOURCE_HIDE',
          resourceType: 'WEBINAR_RESOURCE',
          resourceId: state.resourceId,
          payloadJson: JSON.stringify({ timeNow: Date.now() }),
        });
      }
      const liveSlides = store
        .getState()
        .live.adminResourceStates.filter((s) => s.resourceType === 'WEBINAR_SLIDE' && s.live);
      for (const state of liveSlides) {
        await handleTriggerEvent({
          eventType: 'RESOURCE_HIDE',
          resourceType: 'WEBINAR_SLIDE',
          resourceId: state.resourceId,
          payloadJson: JSON.stringify({ timeNow: Date.now() }),
        });
      }

      setIsScreenShareLive(true);
      setActiveShareScreen(true);
      if (sessionId) {
        markScreenShareIntent(sessionId, Date.now());
      }
    } catch (err) {
      console.error('Partage écran', err);
      const k = classifyScreenShareStartFailure(err);
      if (k === 'cancelled') toast.info(t('live.screenShareStartCancelled'));
      else
        toast.error(
          t(
            k === 'permission'
              ? 'live.screenShareStartPermissionDenied'
              : 'live.screenShareStartFailed',
          ),
        );
    }
  };

  const handleUpdateVideoLayout = async (state: VideoLayoutState) => {
    if (!workspaceId || !webinarId || !sessionId) return;
    const st = store.getState();
    const liveVideoState = st.live.adminResourceStates.find(
      (s) => s.resourceType === 'WEBINAR_RESOURCE' && s.live,
    );
    const resources = st.webinar.webinarResources;
    if (!liveVideoState || !resources?.length) return;
    const videoResource = resources.find((r) => r.id === liveVideoState.resourceId);
    if (!videoResource?.resourceUrl) return;
    try {
      await dispatch(
        updateSessionLayout({
          workspaceId,
          webinarId,
          sessionId,
          input: {
            layout: 'presentation',
            activeAsset: {
              type: 'VIDEO',
              url: videoResource.resourceUrl,
              paused: state.paused,
              muted: state.muted,
              currentTime: state.currentTime,
            },
          },
        }),
      ).unwrap();
    } catch (e) {
      console.error('Échec updateSessionLayout (vidéo de présentation)', e);
    }
  };

  if (!workspaceId || !webinarId || !sessionId || !user?.id || !currentWebinarSession) {
    return <SplashScreen label={t('adminLive.loading')} />;
  }

  if (currentWorkspace?.productAccessStatus === 'CONFIGURATION_ONLY') {
    return <SplashScreen label={t('adminLive.subscriptionNoLive')} fullscreen />;
  }

  if (currentWebinarSession.status === 'FINISHED') {
    return <SplashScreen label={t('adminLive.sessionFinished')} fullscreen />;
  }

  const hasPdfEnLigne = adminResourceStates.some(
    (s) => s.resourceType === 'WEBINAR_SLIDE' && s.live,
  );

  const pageBg = currentWorkspace?.visualIdentity?.backgroundColor || '#FFFFFF';

  const isEvergreenSession = currentWebinarSession.sessionType === 'EVERGREEN';
  const showModeratorLivePage =
    adminChatRole === 'moderator' || (isEvergreenSession && adminChatRole === 'admin');

  if (isEvergreenSession && currentWebinarSession.status === 'UPCOMING') {
    return <SplashScreen label={t('adminLive.evergreenNotStarted')} fullscreen />;
  }

  // Un modérateur sur compte non payant n'a aucun live à rejoindre → pop-up d'abonnement.
  if (adminChatRole === 'moderator' && isFreeWithoutLiveAccess) {
    return (
      <>
        <SplashScreen label={t('adminLive.subscriptionRequired')} fullscreen />
        <SubscriptionRequiredModal
          open
          onOpenChange={() => {}}
          onSubscribed={() => window.location.reload()}
        />
      </>
    );
  }

  if (showModeratorLivePage) {
    const displayedLiveSettings = regieDisplayedSettings(
      liveSettings,
      isEvergreenSession,
      replayedChatModeFor(evergreenChatModeSync.replayed, currentWebinarSession.identifier),
    );
    return (
      <CamLayoutProvider
        workspaceId={workspaceId ?? undefined}
        webinarId={webinarId ?? undefined}
        sessionId={sessionId ?? undefined}
        role="moderator"
      >
        <ModeratorLivePage
          moderatorToken={moderatorToken as ModeratorToken}
          workspaceId={workspaceId}
          webinarId={webinarId}
          sessionId={sessionId}
          visualIdentity={currentWorkspace?.visualIdentity as WorkspaceVisualIdentity}
          handleTriggerEvent={handleTriggerEvent}
          onVideoLayoutChange={handleUpdateVideoLayout}
          handleCloseSession={handleCloseSession}
          currentWebinarSession={currentWebinarSession}
          liveSettings={displayedLiveSettings}
          onLiveSettingsChange={(settings) => {
            const evergreenChatMode = evergreenChatModeCommand(
              settings,
              displayedLiveSettings,
              isEvergreenSession,
            );
            if (evergreenChatMode) {
              void handleEvergreenChatModeChange(evergreenChatMode);
              return;
            }
            handleLiveSettingsChange(
              regieSettingsToSave(settings, isEvergreenSession, liveSettings.chatMode),
            );
          }}
          chatModeUpdating={evergreenChatModeUpdating}
          liveParticipantCount={liveParticipantCount}
          isYouTube={isYouTube}
          youtubeUrl={currentWebinar?.youtubeUrl}
        />
      </CamLayoutProvider>
    );
  }

  return (
    <CamLayoutProvider
      workspaceId={workspaceId ?? undefined}
      webinarId={webinarId ?? undefined}
      sessionId={sessionId ?? undefined}
      role="admin"
    >
      {streamStartStatus === 'PENDING' && !liveConfirmed && <LiveLaunchOverlay />}
      {streamStartStatus === 'FAILED' && !liveConfirmed && !launchFailedDismissed && (
        <LiveLaunchOverlay
          variant="failed"
          onRetry={handleStartSession}
          onClose={() => {
            setLaunchFailedDismissed(true);
            setStartButtonDisabledByOtherAdmin(false);
          }}
        />
      )}
      <Dialog open={preliveLive} onOpenChange={setPreliveLive}>
        <DialogContent className="max-w-3xl" onOpenAutoFocus={(e) => e.preventDefault()}>
          {currentStep === 1 && (
            <DialogHeader className="text-left">
              <DialogTitle>{t('adminLive.checklist.title')}</DialogTitle>
              <DialogDescription>{t('adminLive.checklist.description')}</DialogDescription>
            </DialogHeader>
          )}
          {currentStep === 2 && (
            <DialogHeader className="text-left">
              <DialogTitle>{t('adminLive.setupCheck.title')}</DialogTitle>
              <DialogDescription>{t('adminLive.setupCheck.description')}</DialogDescription>
            </DialogHeader>
          )}
          {currentStep === 3 && (
            <DialogHeader className="text-left">
              <DialogTitle>{t('adminLive.readyStep.title')}</DialogTitle>
              <DialogDescription>
                {t('adminLive.readyStep.description')} <br /> <br />{' '}
                {t('adminLive.readyStep.goodLuck')}
              </DialogDescription>
            </DialogHeader>
          )}
          {currentStep === 1 && (
            <div>
              <Accordion
                type="single"
                collapsible
                defaultValue="ressources-informatiques"
                className="space-y-4"
              >
                <AccordionItem value="ressources-informatiques" className="bg-medium ">
                  <AccordionTrigger className=" rounded-[20px] p-4 ">
                    <Title level={3} className="font-semibold!">
                      <span className="text-primary">1.</span> {t('adminLive.checklist.step1Title')}
                    </Title>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col items-center gap-4 rounded-[20px] bg-medium p-4 pt-0 ">
                    <div className="w-full">
                      <p>{t('adminLive.checklist.step1Content')}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="prefer-headphones" className="bg-medium ">
                  <AccordionTrigger className=" rounded-[20px] p-4 ">
                    <Title level={3} className="font-semibold!">
                      <span className="text-primary">2.</span> {t('adminLive.checklist.step2Title')}
                    </Title>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col items-center gap-4 rounded-[20px] bg-medium p-4 pt-0 ">
                    <div className="w-full">
                      <p>{t('adminLive.checklist.step2Content')}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="avoid-distractions" className="bg-medium ">
                  <AccordionTrigger className=" rounded-[20px] p-4 ">
                    <Title level={3} className="font-semibold!">
                      <span className="text-primary">3.</span> {t('adminLive.checklist.step3Title')}
                    </Title>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col items-center gap-4 rounded-[20px] bg-medium p-4 pt-0 ">
                    <div className="w-full">
                      <p>{t('adminLive.checklist.step3Content')}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}
          {currentStep === 2 && (
            <div className="space-y-6">
              <MediaDeviceTester
                theme={currentWorkspace?.visualIdentity?.theme || 'LIGHT'}
                cameraEnabled={cameraEnabled}
                microphoneEnabled={microphoneEnabled}
                setCameraEnabled={setCameraEnabled}
                setMicrophoneEnabled={setMicrophoneEnabled}
                onDevicesChange={setPretestDevices}
              />
              <div className="flex items-center gap-2">
                <Checkbox
                  id="checked-setup"
                  checked={checkedSetup}
                  onCheckedChange={(checked) => setCheckedSetup(checked as boolean)}
                />
                <Label htmlFor="checked-setup">{t('adminLive.setupCheck.confirmed')}</Label>
              </div>
              <p className="text-sm text-secondary">
                {t('adminLive.setupCheck.faqText')}{' '}
                <Link to="#" className="text-black font-semibold">
                  {t('adminLive.setupCheck.faqLink')}
                </Link>
              </p>
            </div>
          )}
          {currentStep === 3 && (
            <div>
              <img
                src="/assets/gotThis.png"
                alt={t('adminLive.readyStep.imageAlt')}
                className="w-full max-h-96 object-cover rounded-2xl"
              />
            </div>
          )}
          <DialogFooter className="flex pt-4 justify-between">
            <div className="flex gap-2 justify-between w-full">
              <Button
                onClick={() => setCurrentStep((prev) => prev - 1)}
                disabled={currentStep === 1}
              >
                {t('adminLive.nav.previous')}
              </Button>
              <Button
                className={currentStep === 3 ? 'hidden' : ''}
                onClick={() => {
                  if (isYouTube) {
                    setPreliveLive(false);
                    return;
                  }
                  setCurrentStep((prev) => prev + 1);
                }}
                disabled={(!checkedSetup && currentStep === 2) || currentStep === 3}
              >
                {t('adminLive.nav.continue')}
              </Button>
              {currentStep === 3 && (
                <Button
                  onClick={() => setPreliveLive(false)}
                  disabled={!preliveLive}
                  className={cn(buttonVariants({ variant: 'default' }))}
                >
                  {t('adminLive.nav.enterRoom')}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmation pour les demandes de rejoindre */}
      <AlertDialog open={showJoinRequestModal} onOpenChange={setShowJoinRequestModal}>
        <AlertDialogContent
          className="max-w-3xl"
          style={{
            backgroundColor: currentWorkspace?.visualIdentity?.backgroundColor || '#FFFFFF',
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              style={{
                color: currentWorkspace?.visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
              }}
            >
              {t('live.joinRequest.title')}
            </AlertDialogTitle>
            <AlertDialogDescription
              style={{
                color: currentWorkspace?.visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
              }}
            >
              {t('live.joinRequest.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleRejectJoinRequest}
              style={{
                backgroundColor:
                  currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#000000',
              }}
            >
              {t('live.joinRequest.decline')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAcceptJoinRequest}
              style={{
                backgroundColor: currentWorkspace?.visualIdentity?.contrastColor || '#000000',
              }}
            >
              {t('live.joinRequest.accept')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* admin */}

      {adminChatRole === 'admin' && !isEvergreenSession && (
        <>
          {/* Fond fixe sur tout le viewport pour éviter le blanc en cas de débordement/scroll */}
          <div
            className="fixed inset-0 -z-10 min-h-screen"
            style={{ backgroundColor: pageBg }}
            aria-hidden
          />
          <div
            className="min-h-screen h-screen max-w-[2400px] mx-auto w-full flex items-stretch justify-center gap-6 overflow-x-hidden"
            style={{ backgroundColor: pageBg }}
          >
            {/* left section */}
            <div className="flex-3 min-w-0 w-full pl-6 py-6 h-full min-h-0 flex flex-col justify-between">
              {/* camera Section */}
              <div className={`min-h-0 h-full flex flex-col  ${hasPdfEnLigne ? '' : 'flex-1'}`}>
                <div
                  className={`w-full relative rounded-2xl min-h-0 h-full ${hasPdfEnLigne ? '' : 'flex-1'} ${isYouTube ? 'mb-4' : ''}`}
                >
                  {isYouTube
                    ? !preliveLive && (
                        <YouTubeHostStage
                          isLive={currentWebinarSession?.status === 'IN_PROGRESS'}
                          youtubeUrl={currentWebinar?.youtubeUrl}
                        />
                      )
                    : !preliveLive && (
                        <PretestDevicesProvider
                          value={pretestDevices}
                          setSelection={setPretestDevices}
                        >
                          <LiveKitRoom
                            roomName={presenterToken?.roomName || ''}
                            token={presenterToken?.token || ''}
                            isAdmin
                            userId={user?.id || ''}
                            workspaceId={workspaceId}
                            webinarId={webinarId}
                            sessionId={sessionId}
                            handleTriggerEvent={handleTriggerEvent}
                            onVideoLayoutChange={handleUpdateVideoLayout}
                            videoPlayerStateRef={videoPlayerStateRef}
                            hasActiveTab={
                              activePresenters ||
                              activeVideos ||
                              activeSlides ||
                              activeShareScreen ||
                              isScreenShareLive
                            }
                            screenShareControlRef={screenShareControlRef}
                            presenterTracksEnabled={presenterTracksEnabled}
                            removedFromLive={removedFromLive}
                            cameraEnabled={cameraEnabled}
                            microphoneEnabled={microphoneEnabled}
                            videoDeviceId={pretestDevices?.cameraId}
                            audioDeviceId={pretestDevices?.microphoneId}
                            onLocalCameraEnabledChange={setCameraEnabled}
                            onLocalMicrophoneEnabledChange={setMicrophoneEnabled}
                            onRoomReady={setLiveKitRoomInstance}
                          />
                        </PretestDevicesProvider>
                      )}
                  {/* reaction animation */}
                  {currentReactions.map((reaction) => (
                    <div
                      key={reaction.id}
                      className="absolute bottom-0 translate-y-[-50%] pointer-events-none z-50 animate-rise-up"
                      style={{
                        left: `${reaction.left}px`,
                      }}
                    >
                      <div className="text-5xl">{reaction.text}</div>
                    </div>
                  ))}
                </div>

                <div>
                  {activeShareScreen && (
                    <div
                      className="w-full mt-4 p-4 rounded-2xl rounded-bl-none space-y-3"
                      style={getConditionalBackgroundStyle({
                        theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                        defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                        defaultIdentityColor:
                          currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                        isActive: true,
                      })}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`text-sm font-medium ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                        >
                          {t('adminLive.panels.screenShareTitle')}
                        </p>
                        <X
                          className={`w-4 h-4 cursor-pointer ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                          onClick={() => setActiveShareScreen(false)}
                        />
                      </div>
                      <p
                        className={`text-sm ${
                          currentWorkspace?.visualIdentity?.theme === 'DARK'
                            ? 'text-white/80'
                            : 'text-black/80'
                        }`}
                      >
                        {t('live.tabScreenSharePreviewHint')}
                      </p>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full sm:w-auto bg-slate-200"
                        onClick={() => {
                          void screenShareControlRef.current?.stopScreenShare();
                          setIsScreenShareLive(false);
                          setActiveShareScreen(false);
                          if (sessionId) {
                            clearScreenShareIntent(sessionId);
                          }
                        }}
                      >
                        {t('adminLive.panels.stopShare')}
                      </Button>
                    </div>
                  )}
                  {activePresenters && (
                    <div
                      className=" w-full mt-4  p-4 rounded-2xl  space-y-2"
                      style={getConditionalBackgroundStyle({
                        theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                        defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                        defaultIdentityColor:
                          currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                        isActive: true,
                      })}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`text-sm font-medium ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                        >
                          {t('adminLive.panels.presentersTitle')}
                        </p>
                        <X
                          className={`w-4 h-4 cursor-pointer ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                          onClick={() => setActivePresenters(false)}
                        />
                      </div>

                      <PresentersList
                        presenters={presenters}
                        speakers={speakers}
                        loading={presentersLoading}
                        workspaceId={workspaceId}
                        webinarId={webinarId}
                        sessionId={sessionId}
                        sessionIdentifier={currentWebinarSession.identifier}
                        disabledForYoutube={isYouTube}
                      />
                    </div>
                  )}

                  {activeVideos && (
                    <div
                      className=" w-full mt-4  p-4 rounded-2xl  space-y-2"
                      style={getConditionalBackgroundStyle({
                        theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                        defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                        defaultIdentityColor:
                          currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                        isActive: true,
                      })}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`text-sm font-medium ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                        >
                          {t('adminLive.panels.projectTitle')}
                        </p>
                        <X
                          className={`w-4 h-4 cursor-pointer ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                          onClick={() => setActiveVideos(false)}
                        />
                      </div>

                      <VideosList
                        videos={videos}
                        loading={webinarResourcesLoading}
                        handleTriggerEvent={handleTriggerEvent}
                        adminResourceStates={adminResourceStates}
                      />
                    </div>
                  )}

                  {slidesMounted && (
                    <div
                      className=" w-full mt-4 p-4 rounded-2xl rounded-br-none space-y-2"
                      style={{
                        ...getConditionalBackgroundStyle({
                          theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                          defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                          defaultIdentityColor:
                            currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                          isActive: true,
                        }),
                        ...(activeSlides ? {} : { display: 'none' }),
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`text-sm font-medium ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                        >
                          {t('adminLive.panels.projectTitle')}
                        </p>
                        <X
                          className={`w-4 h-4 cursor-pointer ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                          onClick={() => setActiveSlides(false)}
                        />
                      </div>

                      <SlidesList
                        slides={
                          webinarSlides?.filter((s) => s.mimeType === 'application/pdf') ?? []
                        }
                        loading={webinarSlidesLoading}
                        sessionId={sessionId}
                        adminResourceStates={adminResourceStates}
                        handleTriggerEvent={handleTriggerEvent}
                      />
                    </div>
                  )}

                  {/* Mode YouTube : la production vidéo est gérée hors Welya, on masque la barre d'outils. */}
                  {!isYouTube && (
                    <div className="flex gap-4 w-full my-4 h-[82px]">
                      <div
                        title={isYouTube ? t('live.youtube.featureUnavailable') : undefined}
                        className={`flex-1 p-4 relative rounded-lg flex flex-col gap-2 items-center justify-center ${
                          isYouTube ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        } ${activeShareScreen ? 'rounded-t-none' : ''} `}
                        onClick={() => {
                          if (isYouTube) return;
                          if (isScreenShareLive) {
                            setActiveShareScreen((open) => !open);
                            handleItemActivation('shareScreen');
                            return;
                          }
                          void startScreenShareFlow();
                        }}
                        style={getConditionalBackgroundStyle({
                          theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                          defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                          defaultIdentityColor:
                            currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                          isActive: activeShareScreen,
                        })}
                      >
                        {activeShareScreen && (
                          <div
                            className="absolute -top-4 left-0 w-full h-4 z-0"
                            style={getConditionalBackgroundStyle({
                              theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                              defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                              defaultIdentityColor:
                                currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                '#C0D3FF',
                              isActive: activeShareScreen,
                            })}
                          >
                            <div className="absolute -bottom-2 -right-6 size-6">
                              <CurvedCorner
                                theme={
                                  currentWorkspace?.visualIdentity?.theme === 'LIGHT'
                                    ? 'LIGHT'
                                    : 'DARK'
                                }
                                corner="top-left"
                                shadowColor={
                                  getConditionalBackgroundStyle({
                                    theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                                    defaultIdentity:
                                      currentWorkspace?.visualIdentity?.defaultIdentity,
                                    defaultIdentityColor:
                                      currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                      '#C0D3FF',
                                    isActive: activeShareScreen,
                                  }).backgroundColor
                                }
                              />
                            </div>
                          </div>
                        )}
                        <div className="relative">
                          <ScreenShareIcon
                            size={24}
                            color={getConditionalIconColor({
                              defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                              secondaryBackgroundColor:
                                currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
                            })}
                          />
                          <LiveResourceBadge count={isScreenShareLive ? 1 : 0} />
                        </div>
                        <p
                          className={`text-sm font-medium ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                        >
                          {t('adminLive.cards.shareScreen')}
                        </p>
                      </div>
                      <div
                        className={`flex-1 p-4 relative rounded-lg flex flex-col gap-2 items-center justify-center cursor-pointer ${
                          activePresenters ? 'rounded-t-none' : ''
                        } `}
                        onClick={() => handleItemActivation('presenters')}
                        style={getConditionalBackgroundStyle({
                          theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                          defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                          defaultIdentityColor:
                            currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                          isActive: activePresenters,
                        })}
                      >
                        {activePresenters && (
                          <div
                            className="absolute -top-4 left-0 w-full h-4 z-0"
                            style={getConditionalBackgroundStyle({
                              theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                              defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                              defaultIdentityColor:
                                currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                '#C0D3FF',
                              isActive: activePresenters,
                            })}
                          >
                            <div className="absolute -bottom-2 -left-6 size-6">
                              <CurvedCorner
                                theme={
                                  currentWorkspace?.visualIdentity?.theme === 'LIGHT'
                                    ? 'LIGHT'
                                    : 'DARK'
                                }
                                corner="top-right"
                                shadowColor={
                                  getConditionalBackgroundStyle({
                                    theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                                    defaultIdentity:
                                      currentWorkspace?.visualIdentity?.defaultIdentity,
                                    defaultIdentityColor:
                                      currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                      '#C0D3FF',
                                    isActive: activeVideos,
                                  }).backgroundColor
                                }
                              />
                            </div>
                            <div className="absolute -bottom-2 -right-6 size-6">
                              <CurvedCorner
                                theme={
                                  currentWorkspace?.visualIdentity?.theme === 'LIGHT'
                                    ? 'LIGHT'
                                    : 'DARK'
                                }
                                corner="top-left"
                                shadowColor={
                                  getConditionalBackgroundStyle({
                                    theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                                    defaultIdentity:
                                      currentWorkspace?.visualIdentity?.defaultIdentity,
                                    defaultIdentityColor:
                                      currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                      '#C0D3FF',
                                    isActive: activeVideos,
                                  }).backgroundColor
                                }
                              />
                            </div>
                          </div>
                        )}

                        <UsersIcon
                          size={24}
                          color={getConditionalIconColor({
                            defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                            secondaryBackgroundColor:
                              currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
                          })}
                        />
                        <p
                          className={`text-sm font-medium ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                        >
                          {t('adminLive.cards.presenters')}
                        </p>
                      </div>

                      <div
                        title={isYouTube ? t('live.youtube.featureUnavailable') : undefined}
                        className={`flex-1 p-4 rounded-lg flex flex-col gap-2 items-center justify-center relative ${
                          isYouTube ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        } ${activeVideos ? 'rounded-t-none' : ''} `}
                        onClick={() => {
                          if (isYouTube) return;
                          handleItemActivation('videos');
                        }}
                        style={getConditionalBackgroundStyle({
                          theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                          defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                          defaultIdentityColor:
                            currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                          isActive: activeVideos,
                        })}
                      >
                        {activeVideos && (
                          <div
                            className="absolute -top-4 left-0 w-full h-4 z-0"
                            style={getConditionalBackgroundStyle({
                              theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                              defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                              defaultIdentityColor:
                                currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                '#C0D3FF',
                              isActive: activeVideos,
                            })}
                          >
                            <div className="absolute -bottom-2 -left-6 size-6">
                              <CurvedCorner
                                theme={
                                  currentWorkspace?.visualIdentity?.theme === 'LIGHT'
                                    ? 'LIGHT'
                                    : 'DARK'
                                }
                                corner="top-right"
                                shadowColor={
                                  getConditionalBackgroundStyle({
                                    theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                                    defaultIdentity:
                                      currentWorkspace?.visualIdentity?.defaultIdentity,
                                    defaultIdentityColor:
                                      currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                      '#C0D3FF',
                                    isActive: activeVideos,
                                  }).backgroundColor
                                }
                              />
                            </div>
                            <div className="absolute -bottom-2 -right-6 size-6">
                              <CurvedCorner
                                theme={
                                  currentWorkspace?.visualIdentity?.theme === 'LIGHT'
                                    ? 'LIGHT'
                                    : 'DARK'
                                }
                                corner="top-left"
                                shadowColor={
                                  getConditionalBackgroundStyle({
                                    theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                                    defaultIdentity:
                                      currentWorkspace?.visualIdentity?.defaultIdentity,
                                    defaultIdentityColor:
                                      currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                      '#C0D3FF',
                                    isActive: activeVideos,
                                  }).backgroundColor
                                }
                              />
                            </div>
                          </div>
                        )}

                        <div className="flex flex-col items-center justify-center gap-2 z-20">
                          <div className="relative">
                            <VideoIcon
                              size={24}
                              color={getConditionalIconColor({
                                defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                                secondaryBackgroundColor:
                                  currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
                              })}
                            />
                            <LiveResourceBadge
                              count={
                                adminResourceStates.filter(
                                  (s) => s.resourceType === 'WEBINAR_RESOURCE' && s.live,
                                ).length
                              }
                            />
                          </div>
                          <p
                            className={`text-sm font-medium ${
                              currentWorkspace?.visualIdentity?.theme === 'DARK'
                                ? 'text-white'
                                : 'text-black'
                            }`}
                          >
                            {t('adminLive.cards.videos')}
                          </p>
                        </div>
                      </div>

                      <div
                        title={isYouTube ? t('live.youtube.featureUnavailable') : undefined}
                        className={`flex-1 p-4 rounded-lg flex flex-col gap-2 items-center justify-center relative ${
                          isYouTube ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        } ${activeSlides ? 'rounded-t-none' : ''} `}
                        onClick={() => {
                          if (isYouTube) return;
                          handleItemActivation('slides');
                        }}
                        style={getConditionalBackgroundStyle({
                          theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                          defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                          defaultIdentityColor:
                            currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                          isActive: activeSlides,
                        })}
                      >
                        {activeSlides && (
                          <div
                            className="absolute -top-4 left-0 w-full h-4 z-0"
                            style={getConditionalBackgroundStyle({
                              theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                              defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                              defaultIdentityColor:
                                currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                '#C0D3FF',
                              isActive: activeSlides,
                            })}
                          >
                            <div className="absolute -bottom-2 -left-6 size-6">
                              <CurvedCorner
                                theme={
                                  currentWorkspace?.visualIdentity?.theme === 'LIGHT'
                                    ? 'LIGHT'
                                    : 'DARK'
                                }
                                corner="top-right"
                                shadowColor={
                                  getConditionalBackgroundStyle({
                                    theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                                    defaultIdentity:
                                      currentWorkspace?.visualIdentity?.defaultIdentity,
                                    defaultIdentityColor:
                                      currentWorkspace?.visualIdentity?.secondaryBackgroundColor ||
                                      '#C0D3FF',
                                    isActive: activeSlides,
                                  }).backgroundColor
                                }
                              />
                            </div>
                          </div>
                        )}
                        <div className="relative">
                          <TutorialIcon
                            size={24}
                            color={getConditionalIconColor({
                              defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                              secondaryBackgroundColor:
                                currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
                            })}
                          />
                          <LiveResourceBadge
                            count={
                              adminResourceStates.filter(
                                (s) => s.resourceType === 'WEBINAR_SLIDE' && s.live,
                              ).length
                            }
                          />
                        </div>
                        <p
                          className={`text-sm font-medium ${
                            currentWorkspace?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }`}
                        >
                          {t('adminLive.cards.slides')}
                        </p>
                      </div>
                    </div>
                  )}
                  {isLiveRoomFull(usage, liveParticipantCount) && usage?.participants && (
                    <div className="bg-warning-light flex gap-2 rounded-lg w-full border-2 border-warning my-6 p-4">
                      <Info size={24} color="#FFAF15" className="" />
                      <div className="w-full flex flex-col gap-2">
                        <p className="text-xl">
                          {t('adminLive.billing.roomFullTitle')}{' '}
                          {t('adminLive.billing.roomFullWaiting', {
                            count: Math.max(0, liveParticipantCount - usage.participants.max),
                          })}
                        </p>
                        <div className="flex items-center gap-2 justify-between">
                          <p className="text-sm ">
                            {t('adminLive.billing.roomFullDescription', {
                              max: usage.participants.max,
                            })}
                          </p>
                          <ChangePlanDialog />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* UPGRADE: liste d'attente pleine ou session terminée dans X minutes */}
                  {remainingSessionSeconds !== null &&
                    remainingSessionSeconds <= SESSION_ENDING_SOON_SECONDS && (
                      <div className="bg-warning-light flex gap-2 rounded-lg w-full border-2 border-warning my-6 p-4">
                        <Hourglass
                          size={24}
                          color="#FFAF15"
                          className=" animate-spin animation-duration-5000"
                        />
                        <div className="w-full flex flex-col gap-2">
                          <p className="text-xl">
                            {t('adminLive.billing.sessionEndingSoonTitle', {
                              time: formatRemainingTime(remainingSessionSeconds),
                            })}
                          </p>
                          <div className="flex items-center gap-2 justify-between">
                            <p className="text-sm ">
                              {t('adminLive.billing.sessionEndingSoonDescription', {
                                max: usage?.sessionDuration?.max ?? 120,
                              })}
                            </p>
                            <ChangePlanDialog />
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* footer */}
              <div>
                {channelName && (
                  <FooterBar
                    liveParticipantCount={liveParticipantCount}
                    handleStartSession={handleStartSession}
                    handleCloseSession={handleCloseSession}
                    startSessionLoading={startSessionLoading || startCustomRecordingLoading}
                    launchPending={streamStartStatus === 'PENDING' && !liveConfirmed}
                    startButtonDisabledByOtherAdmin={startButtonDisabledByOtherAdmin}
                    liveStartAt={
                      liveConfirmed ? (currentWebinarSession?.startedAt ?? undefined) : undefined
                    }
                    visualIdentity={currentWorkspace?.visualIdentity as WorkspaceVisualIdentity}
                    formattedPlannedDate={formattedPlannedDate}
                    showSettings
                    settings={liveSettings}
                    onSettingsChange={handleLiveSettingsChange}
                    session={currentWebinarSession}
                    upgradeSlot={
                      isFreeWithoutLiveAccess ? (
                        <UsageExpiredAlert
                          isConfig
                          triggerClassName="text-xs xl:text-sm"
                          triggerLabel={tCommon('liveSettings.startSessionUpgradeButton')}
                          onSubscribed={() => window.location.reload()}
                        />
                      ) : undefined
                    }
                  />
                )}
              </div>
            </div>
            {/* right section */}
            <div className="flex-1  pt-6 pr-6  h-svh flex flex-col min-h-0 gap-6 ">
              <div className="  h-full flex flex-col  min-h-0">
                {/* icone top */}
                <div className="flex items-center justify-between gap-4  shrink-0">
                  <IconContainer
                    icon={
                      <div className="relative">
                        <ListIcon
                          size={24}
                          color={getConditionalIconColor({
                            defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                            secondaryBackgroundColor:
                              currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
                          })}
                        />
                        <LiveResourceBadge
                          count={
                            adminResourceStates.filter(
                              (s) => s.resourceType === 'SURVEY_TEMPLATE' && s.live,
                            ).length
                          }
                        />
                      </div>
                    }
                    isActive={activeList}
                    onClick={() => handleIconToggle('list')}
                    position="first"
                    label={t('sessionPage.surveys')}
                  />

                  <IconContainer
                    icon={
                      <div className="relative">
                        <QuestionIcon
                          size={24}
                          color={getConditionalIconColor({
                            defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                            secondaryBackgroundColor:
                              currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
                          })}
                        />
                        <LiveResourceBadge
                          count={
                            adminResourceStates.filter(
                              (s) => s.resourceType === 'QUIZ_TEMPLATE' && s.live,
                            ).length
                          }
                        />
                      </div>
                    }
                    isActive={activeQuestion}
                    onClick={() => handleIconToggle('question')}
                    position="middle"
                    label={t('sessionPage.questions')}
                  />

                  <IconContainer
                    icon={
                      <div className="relative">
                        <Paperclip
                          size={24}
                          color={getConditionalIconColor({
                            defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                            secondaryBackgroundColor:
                              currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
                          })}
                        />
                        <LiveResourceBadge
                          count={
                            adminResourceStates.filter(
                              (s) => s.resourceType === 'SHARED_RESOURCE' && s.live,
                            ).length
                          }
                        />
                      </div>
                    }
                    isActive={activeFolder}
                    onClick={() => handleIconToggle('folder')}
                    position="middle"
                    label={t('sessionPage.resources')}
                  />

                  <IconContainer
                    icon={
                      <div className="relative">
                        <GiftIcon
                          size={24}
                          color={getConditionalIconColor({
                            defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                            secondaryBackgroundColor:
                              currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
                          })}
                        />
                        <LiveResourceBadge
                          count={
                            adminResourceStates.filter(
                              (s) => s.resourceType === 'OFFER_TEMPLATE' && s.live,
                            ).length
                          }
                        />
                      </div>
                    }
                    isActive={activeGift}
                    onClick={() => handleIconToggle('gift')}
                    position="last"
                    label={t('sessionPage.offers')}
                  />
                </div>

                {/* section content */}

                {/* panneaux toujours montés, masqués quand l'onglet est replié : les démonter couperait leur abonnement Ably et figerait les résultats */}
                {workspaceId && webinarId && currentWorkspace?.visualIdentity && (
                  <div
                    className=" mt-4 h-full rounded-tr-2xl p-4 overflow-auto"
                    style={{
                      ...getConditionalBackgroundStyle({
                        theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                        defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                        defaultIdentityColor:
                          currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                        isActive: activeList,
                      }),
                      ...(activeList ? {} : { display: 'none' }),
                    }}
                  >
                    <SurveysList
                      surveys={surveys}
                      loading={surveyTemplatesLoading}
                      visualIdentity={currentWorkspace.visualIdentity}
                      workspaceId={workspaceId}
                      webinarId={webinarId}
                      sessionId={sessionId ?? undefined}
                      adminResourceStates={adminResourceStates}
                      handleTriggerEvent={handleTriggerEvent}
                      liveParticipantCountSync={liveParticipantCount}
                    />
                  </div>
                )}
                {workspaceId && webinarId && currentWorkspace?.visualIdentity && (
                  <div
                    className=" mt-4 h-full rounded-t-2xl p-4 overflow-auto"
                    style={{
                      ...getConditionalBackgroundStyle({
                        theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                        defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                        defaultIdentityColor:
                          currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                        isActive: activeQuestion,
                      }),
                      ...(activeQuestion ? {} : { display: 'none' }),
                    }}
                  >
                    <QuizsList
                      quizs={quizs}
                      visualIdentity={currentWorkspace.visualIdentity}
                      workspaceId={workspaceId}
                      webinarId={webinarId}
                      sessionId={sessionId ?? undefined}
                      adminResourceStates={adminResourceStates}
                      handleTriggerEvent={handleTriggerEvent}
                      liveParticipantCountSync={liveParticipantCount}
                    />
                  </div>
                )}
                {/* seul panneau encore démonté au repliement : son chargement au montage sert de
                    rafraîchissement, et il porte sa propre pagination */}
                {activeFolder && workspaceId && webinarId && currentWorkspace?.visualIdentity && (
                  <div
                    className=" mt-4 h-full rounded-t-2xl p-4 overflow-auto"
                    style={getConditionalBackgroundStyle({
                      theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                      defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                      defaultIdentityColor:
                        currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                      isActive: activeFolder,
                    })}
                  >
                    <SharedResourceLiveList
                      workspaceId={workspaceId}
                      webinarId={webinarId}
                      sessionId={sessionId ?? undefined}
                      sessionIdentifier={currentWebinarSession?.identifier ?? ''}
                      visualIdentity={currentWorkspace.visualIdentity}
                    />
                  </div>
                )}
                {workspaceId && webinarId && currentWorkspace?.visualIdentity && (
                  <div
                    className=" mt-4 h-full rounded-tl-2xl p-4 overflow-auto"
                    style={{
                      ...getConditionalBackgroundStyle({
                        theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                        defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                        defaultIdentityColor:
                          currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                        isActive: activeGift,
                      }),
                      ...(activeGift ? {} : { display: 'none' }),
                    }}
                  >
                    <OfferList
                      offers={offers}
                      visualIdentity={currentWorkspace.visualIdentity}
                      workspaceId={workspaceId}
                      webinarId={webinarId}
                      sessionId={sessionId ?? undefined}
                      adminResourceStates={adminResourceStates}
                      handleTriggerEvent={handleTriggerEvent}
                      liveParticipantCountSync={liveParticipantCount}
                    />
                  </div>
                )}

                {/* section chat - toujours monté pour préserver l'état des messages, masqué quand un autre panneau est actif */}
                <div
                  className="mt-2 rounded-t-4 flex-1 flex flex-col min-h-0"
                  style={
                    activeLike || activeList || activeQuestion || activeFolder || activeGift
                      ? { display: 'none' }
                      : undefined
                  }
                >
                  <div className="w-full pt-2 flex-1 min-h-0">
                    <Tabs
                      className=" rounded-t-lg h-full flex flex-col min-h-0"
                      value={activeTab}
                      onValueChange={setActiveTab}
                    >
                      <TabsList
                        className="w-full shrink-0"
                        style={getConditionalBackgroundStyle({
                          theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
                          defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
                          defaultIdentityColor:
                            currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0FFEA',
                        })}
                      >
                        <TabsTrigger
                          value="chat"
                          style={{
                            color: theme === 'DARK' ? '#FFFFFF' : '#000000',
                            borderBottomColor:
                              activeTab === 'chat'
                                ? currentWorkspace?.visualIdentity?.defaultIdentity === true
                                  ? '#6DFFCD'
                                  : contrastColor
                                : secondaryBackgroundColor,
                          }}
                        >
                          {t('adminLive.tabs.chat')}
                        </TabsTrigger>
                        <TabsTrigger
                          value="viewers"
                          style={{
                            color: theme === 'DARK' ? '#FFFFFF' : '#000000',
                            borderBottomColor:
                              activeTab === 'viewers'
                                ? currentWorkspace?.visualIdentity?.defaultIdentity === true
                                  ? '#6DFFCD'
                                  : contrastColor
                                : secondaryBackgroundColor,
                          }}
                        >
                          {t('adminLive.tabs.participants')}
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent
                        value="chat"
                        className="h-full flex flex-col min-h-0 data-[state=inactive]:hidden"
                        forceMount={true}
                      >
                        <ChatView
                          workspaceId={workspaceId}
                          webinarId={webinarId}
                          sessionId={sessionId}
                          chatMode={liveSettings.chatMode}
                          channelName={channelName || ''}
                          userId={user.id}
                          userName={user.firstName}
                          isAdmin
                          liveParticipantCountSync={liveParticipantCount}
                          visualIdentity={
                            currentWorkspace?.visualIdentity as WorkspaceVisualIdentity
                          }
                        />
                      </TabsContent>
                      <TabsContent value="viewers" className=" h-full flex flex-col min-h-0">
                        {workspaceId && webinarId && sessionId && (
                          <AttendeesListView
                            workspaceId={workspaceId}
                            webinarId={webinarId}
                            sessionId={sessionId}
                            visualIdentity={
                              currentWorkspace?.visualIdentity as WorkspaceVisualIdentity
                            }
                          />
                        )}
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </CamLayoutProvider>
  );
}
